import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { DatePickerField } from "../components/DatePickerField";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { TicketMessaggi } from "../components/TicketMessaggi";
import { AttachmentGallery } from "../components/AttachmentGallery";
import { DocumentUploader } from "../components/DocumentUploader";
import { CrediusAtti } from "../components/CrediusAtti";
import { CrediusModificaPratica } from "../components/CrediusModificaPratica";
import { CrediusRecuperi } from "../components/CrediusRecuperi";
import { CrediusDiffide } from "../components/CrediusDiffide";
import { toast } from "sonner";
import {
  Scale, ArrowLeft, Gavel, Building2, User, Users, Loader2, Plus, Trash2, Send, Download,
  History, FolderOpen, Folder, FolderInput, FolderPlus, RefreshCw, ClipboardList, FileDown, FileText, FileSignature, Pencil, StickyNote, AlertCircle, ShieldOff, Wallet,
  ChevronDown, ChevronUp, Eye, EyeOff, Handshake,
} from "lucide-react";

const SEL = "h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white";
const STATO_STYLE = {
  "Da affidare": "bg-slate-200 text-slate-700 border-slate-400",
  "Preventivo": "bg-sky-100 text-sky-800 border-sky-300",
  Affidata: "bg-amber-100 text-amber-800 border-amber-300",
  "In gestione": "bg-sky-100 text-sky-800 border-sky-300",
  Conclusa: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const TIPO_ICON = { condominio: Building2, condomino: User, soggetto: Users };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function Card({ title, icon: Icon, children, testid, collapsible = false, defaultOpen = true, countBadge = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testid} className="bg-white rounded-xl border-2 border-slate-200 p-4 sm:p-5">
      {collapsible ? (
        <button type="button" data-testid={testid ? `${testid}-toggle` : undefined}
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 font-bold text-indigo-950 mb-3">
          <span className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-indigo-800" /> {title}
            {countBadge != null && (
              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{countBadge}</span>
            )}
          </span>
          {open ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
        </button>
      ) : (
        <div className="flex items-center gap-2 font-bold text-indigo-950 mb-3">
          <Icon className="w-5 h-5 text-indigo-800" /> {title}
        </div>
      )}
      {(!collapsible || open) && children}
    </div>
  );
}

function PartiList({ label, parti }) {
  // Iter122k134 · liste parti con più di 2 soggetti collassabili di default per ridurre l'ingombro
  const totale = (parti || []).length;
  const isLong = totale > 2;
  const [expanded, setExpanded] = useState(false);
  const testidKey = (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-bold uppercase text-slate-500">{label}</div>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
            data-testid={`credius-parti-toggle-${testidKey}`}
          >
            {expanded ? (
              <>Nascondi <ChevronUp className="w-3.5 h-3.5" /></>
            ) : (
              <>Mostra {totale} soggetti <ChevronDown className="w-3.5 h-3.5" /></>
            )}
          </button>
        )}
      </div>
      {totale === 0 ? <div className="text-sm text-slate-400">—</div> : isLong && !expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 w-full text-left px-2 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 text-xs font-semibold text-indigo-900 hover:bg-indigo-100"
          data-testid={`credius-parti-summary-${testidKey}`}
        >
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {totale} soggetti — clicca per espandere
          </span>
        </button>
      ) : (
        <div className="flex flex-col gap-1.5 mt-1">
          {parti.map((p, i) => {
            const Icon = TIPO_ICON[p.tipo] || Users;
            const hasFiscal = p.tipo === "condominio" && (p.codice_fiscale || p.codice_sdi);
            const amm = p.amministratore;
            const hasAmm = !!(amm && (amm.nome || amm.email || amm.telefono || amm.pec));
            // Iter122k46 · Dati completi per TUTTI i tipi di parte (condominio + soggetto + fornitore + condomino)
            const indirizzoRiga = [p.indirizzo, p.cap, p.citta, p.provincia].filter(Boolean).join(" ");
            const hasContatti = !!(p.telefono || p.email || p.pec || p.referente || indirizzoRiga || p.codice_fiscale || p.partita_iva);
            const isRelevantList = label === "Clienti" || label === "Cliente/i" || label === "Controparti" || label === "Controparte/i";
            const dettaglioLabel = p.tipo === "condominio" ? "Dati cliente"
              : p.tipo === "soggetto" ? `Dati soggetto${p.tipo_persona ? ` · ${p.tipo_persona}` : ""}`
              : p.tipo === "fornitore" ? "Dati fornitore"
              : p.tipo === "condomino" ? "Dati condòmino"
              : "Dati";
            const showDetails = isRelevantList && hasContatti;
            return (
              <div key={i} className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-xs font-semibold text-indigo-900 self-start">
                  <Icon className="w-3.5 h-3.5" />{p.nome}
                  {hasFiscal && (
                    <span className="text-[10px] font-normal text-indigo-600 ml-1" data-testid={`credius-cond-fiscal-${p.ref_id}`}>
                      {p.codice_fiscale ? `· CF ${p.codice_fiscale}` : ""}{p.codice_sdi ? ` · SDI ${p.codice_sdi}` : ""}
                    </span>
                  )}
                </span>
                {showDetails && (
                  <div className="ml-2 text-[11px] text-slate-600 border-l-2 border-indigo-300 pl-2"
                       data-testid={`credius-parte-dati-${p.tipo}-${p.ref_id}`}>
                    <div className="font-bold text-indigo-800 uppercase text-[10px] tracking-wide">{dettaglioLabel}</div>
                    {p.referente && <div className="text-slate-700"><b>Referente:</b> {p.referente}</div>}
                    {p.categoria && <div className="text-slate-700"><b>Categoria:</b> {p.categoria}</div>}
                    {(p.codice_fiscale || p.partita_iva) && (
                      <div className="text-slate-700">
                        {p.codice_fiscale && <span>CF <span className="font-mono">{p.codice_fiscale}</span></span>}
                        {p.codice_fiscale && p.partita_iva && <span> · </span>}
                        {p.partita_iva && <span>P.IVA <span className="font-mono">{p.partita_iva}</span></span>}
                      </div>
                    )}
                    {indirizzoRiga && <div className="text-slate-700">{indirizzoRiga}</div>}
                    <div className="text-slate-500 flex flex-wrap gap-x-2">
                      {p.telefono && <span data-testid={`credius-parte-tel-${p.ref_id}`}>Tel. {p.telefono}</span>}
                      {p.email && <span>{p.email}</span>}
                      {p.pec && <span>PEC: {p.pec}</span>}
                    </div>
                    {p.note && <div className="text-slate-500 italic mt-0.5"><b>Note:</b> {p.note}</div>}
                  </div>
                )}
                {/* Iter122k48 · Dati veicolo (se presenti sull'anagrafica) */}
                {p.dati_veicolo && (p.dati_veicolo.targa || p.dati_veicolo.modello || p.dati_veicolo.proprietario?.nome || p.dati_veicolo.assicurato?.nome) && (
                  <div className="ml-2 text-[11px] text-slate-600 border-l-2 border-amber-400 pl-2" data-testid={`credius-parte-veicolo-${p.ref_id}`}>
                    <div className="font-bold text-amber-800 uppercase text-[10px] tracking-wide">Dati veicolo</div>
                    {(p.dati_veicolo.targa || p.dati_veicolo.modello) && (
                      <div className="text-slate-700">
                        {p.dati_veicolo.targa && <span className="font-mono font-bold">{p.dati_veicolo.targa}</span>}
                        {p.dati_veicolo.targa && p.dati_veicolo.modello && <span> · </span>}
                        {p.dati_veicolo.modello && <span>{p.dati_veicolo.modello}</span>}
                      </div>
                    )}
                    {p.dati_veicolo.proprietario?.nome && (
                      <div className="text-slate-700"><b>Proprietario:</b> {p.dati_veicolo.proprietario.nome}
                        {p.dati_veicolo.proprietario.codice_fiscale && <span className="font-mono"> · {p.dati_veicolo.proprietario.codice_fiscale}</span>}
                      </div>
                    )}
                    {p.dati_veicolo.assicurato?.nome && (
                      <div className="text-slate-700"><b>Assicurato:</b> {p.dati_veicolo.assicurato.nome}
                        {p.dati_veicolo.assicurato.codice_fiscale && <span className="font-mono"> · {p.dati_veicolo.assicurato.codice_fiscale}</span>}
                      </div>
                    )}
                    {p.dati_veicolo.conducente?.nome && (
                      <div className="text-slate-700"><b>Conducente:</b> {p.dati_veicolo.conducente.nome}
                        {p.dati_veicolo.conducente.codice_fiscale && <span className="font-mono"> · {p.dati_veicolo.conducente.codice_fiscale}</span>}
                      </div>
                    )}
                  </div>
                )}
                {/* Iter122k48 · Dati assicurativi (se presenti sull'anagrafica) */}
                {p.dati_assicurativi && (p.dati_assicurativi.numero_polizza || p.dati_assicurativi.compagnia || p.dati_assicurativi.agenzia) && (
                  <div className="ml-2 text-[11px] text-slate-600 border-l-2 border-sky-400 pl-2" data-testid={`credius-parte-assicurativi-${p.ref_id}`}>
                    <div className="font-bold text-sky-800 uppercase text-[10px] tracking-wide">Dati assicurativi</div>
                    {p.dati_assicurativi.numero_polizza && <div className="text-slate-700"><b>Polizza n.:</b> <span className="font-mono">{p.dati_assicurativi.numero_polizza}</span></div>}
                    {p.dati_assicurativi.compagnia && <div className="text-slate-700"><b>Compagnia:</b> {p.dati_assicurativi.compagnia}</div>}
                    {p.dati_assicurativi.agenzia && <div className="text-slate-700"><b>Agenzia:</b> {p.dati_assicurativi.agenzia}</div>}
                  </div>
                )}
                {hasAmm && (
                  <div className="ml-2 text-[11px] text-slate-600 border-l-2 border-indigo-300 pl-2" data-testid={`credius-parti-amministratore-${p.ref_id}`}>
                    <div className="font-bold text-indigo-800 uppercase text-[10px] tracking-wide">Amministratore p.t.</div>
                    <div className="text-slate-700">
                      {amm.nome && <span className="font-semibold">{amm.nome}</span>}
                      {amm.codice_fiscale && <span className="ml-1 font-mono">· CF {amm.codice_fiscale}</span>}
                      {amm.partita_iva && <span className="ml-1 font-mono">· P.IVA {amm.partita_iva}</span>}
                    </div>
                    <div className="text-slate-500 flex flex-wrap gap-x-2">
                      {amm.email && <span>{amm.email}</span>}
                      {amm.pec && <span>PEC: {amm.pec}</span>}
                      {amm.telefono && <span>Tel. {amm.telefono}</span>}
                      {(amm.indirizzo || amm.citta) && (
                        <span>{[amm.indirizzo, amm.cap, amm.citta, amm.provincia].filter(Boolean).join(" ")}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CrediusDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [p, setP] = useState(null);
  const [meta, setMeta] = useState({});
  const [nuovoStato, setNuovoStato] = useState("");
  const [notaStato, setNotaStato] = useState("");
  const [attivita, setAttivita] = useState("");
  // Iter122al · attività: allegati + visibilità cliente (opt-out per pratiche esterne)
  const [attDocs, setAttDocs] = useState([]);
  const [attExpanded, setAttExpanded] = useState(false);
  const [attVisibile, setAttVisibile] = useState(true);
  // Iter122an · classificazione parte (verde) vs controparte (rosso)
  const [attParte, setAttParte] = useState("parte");
  // Iter122ao · filtro feed cronologia
  const [feedFilter, setFeedFilter] = useState("all"); // "all" | "parte" | "controparte"
  // Iter122ap · filtro sezione documentale
  const [docFilter, setDocFilter] = useState("all"); // "all" | "parte" | "controparte"
  const [showUpload, setShowUpload] = useState(false);
  // Iter122k30/31 · sezione Documentazione collassabile (tendina)
  const [docsSectionOpen, setDocsSectionOpen] = useState(true);
  // Iter122k70 · Modalità raggruppamento cartelle: "tipo" | "soggetto" | "cartella"
  const [docsGroupBy, setDocsGroupBy] = useState("tipo");
  // Iter122k70 · Cartelle aperte (chiuse di default) — chiave = "modo:folder-key"
  const [openFolders, setOpenFolders] = useState({});
  const toggleFolder = (k) => setOpenFolders((s) => ({ ...s, [k]: !s[k] }));
  // Iter122k70 · popup "sposta in cartella" per singolo documento
  const [movingDoc, setMovingDoc] = useState(null); // {doc, cartella, soggetto_nome}
  // Iter122k74 · rinomina cartella custom
  const [renamingFolder, setRenamingFolder] = useState(null); // string oldName | null
  // Iter122k102 · crea cartella custom vuota
  const [creatingFolder, setCreatingFolder] = useState(false);
  // Iter123k180 · parent folder quando si crea una sottocartella (null = top-level)
  const [creatingSubIn, setCreatingSubIn] = useState(null);
  // Iter122k158 · Multi-selezione documenti per download ZIP
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [zipDownloading, setZipDownloading] = useState(false);
  const toggleDocSelected = (docId) => setSelectedDocs((s) => {
    const n = new Set(s); if (n.has(docId)) n.delete(docId); else n.add(docId); return n;
  });
  const clearDocSelection = () => setSelectedDocs(new Set());
  const scaricaZipSelezionati = async () => {
    if (selectedDocs.size === 0) return;
    setZipDownloading(true);
    try {
      const r = await api.post(`/credius/pratiche/${id}/documenti/zip`,
        { doc_ids: Array.from(selectedDocs) }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `credius-${p.numero || id.slice(0, 8)}-documenti.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Scaricato archivio con ${selectedDocs.size} documento/i`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download ZIP fallito");
    } finally { setZipDownloading(false); }
  };
  const deleteCartella = async (nome) => {
    if (!window.confirm(`Eliminare la cartella «${nome}»?`)) return;
    try {
      const r = await api.delete(`/credius/pratiche/${id}/cartelle/${encodeURIComponent(nome)}`);
      setP(r.data);
      toast.success("Cartella eliminata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };
  // Iter122k101 · Drag & drop: sposta doc tra cartelle senza aprire popup
  const [dragOverKey, setDragOverKey] = useState(null); // "modo:folderName" evidenziato
  const [draggingDocId, setDraggingDocId] = useState(null);
  const canMoveDoc = (d) => isAdmin || d.caricato_ruolo === "Fornitore";
  const dropOnFolder = async (docId, folderName, mode) => {
    setDragOverKey(null); setDraggingDocId(null);
    const doc = (p.documenti || []).find((x) => x.id === docId);
    if (!doc) return;
    if (!canMoveDoc(doc)) { toast.error("Puoi spostare solo i documenti caricati da te"); return; }
    // Costruisce il payload in base alla modalità di raggruppamento
    let payload = {};
    if (mode === "tipo") {
      if ((doc.tipo || "Altro") === folderName) return;
      payload = { tipo: folderName };
    } else if (mode === "cartella") {
      const target = folderName === "Non archiviati" ? "" : folderName;
      if ((doc.cartella || "") === target) return;
      payload = { cartella: target };
    } else if (mode === "soggetto") {
      // "Parte: Nome" / "Controparte: Nome" / "Parte (senza nominativo)"
      const isCtrp = folderName.startsWith("Controparte");
      const parte = isCtrp ? "controparte" : "parte";
      const nome = folderName.includes(":") ? folderName.split(":").slice(1).join(":").trim() : "";
      if ((doc.parte || "parte") === parte && (doc.soggetto_nome || "") === nome) return;
      payload = { parte, soggetto_nome: nome };
    }
    try {
      await api.put(`/credius/pratiche/${id}/documenti/${docId}/organizza`, payload);
      toast.success(`Spostato in "${folderName}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spostamento non riuscito");
    }
  };
  // Iter122k77 · cliente esterno: upload documento firmato (con o senza riferimento a un doc esistente)
  const [clientUpload, setClientUpload] = useState(null); // { refDocId, refFilename, files:[{id,filename}], nota, busy }
  // Iter122k31 · download file autenticato (l'endpoint /api/files/{id} accetta solo header
  // Authorization, non il query param ?auth) → scarica via axios (Bearer) e crea object URL
  const openFile = async (d) => {
    try {
      const res = await api.get(`/files/${d.file_id || d.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || "documento";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("Impossibile aprire il file");
    }
  };
  const [docs, setDocs] = useState([]);
  const [docTipo, setDocTipo] = useState("Altro");
  const [docRiservato, setDocRiservato] = useState(false);
  // Iter122k24 · Task 5 · mappa file_id → visibile? per gestione visibilità PER-FILE
  const [docVisibilita, setDocVisibilita] = useState({});
  // Iter122an · classificazione parte/controparte del documento
  const [docParte, setDocParte] = useState("parte");
  // Iter122k70 · nuovi campi upload: nominativo (parti/controparti pratica) + cartella custom
  const [docSoggetto, setDocSoggetto] = useState("");
  const [docCartella, setDocCartella] = useState("");
  // Iter122ag · editor inline co-affidatario
  const [editCoaff, setEditCoaff] = useState(false);
  const [coaffSel, setCoaffSel] = useState("");
  const [busy, setBusy] = useState(false);
  const [rifiutoMode, setRifiutoMode] = useState(false);
  const [rifiutoMotivo, setRifiutoMotivo] = useState("");
  const [riaffidaId, setRiaffidaId] = useState("");
  const [showModifica, setShowModifica] = useState(false);
  const [showRevoca, setShowRevoca] = useState(false);
  const [noteStudio, setNoteStudio] = useState("");
  // Iter122ai · Step 1 rev · tab switcher (solo pratiche interne, admin)
  const [detailTab, setDetailTab] = useState("gestione"); // "gestione" | "contabilita"

  const load = useCallback(() => {
    api.get(`/credius/pratiche/${id}`).then((r) => {
      setP(r.data); setNuovoStato(r.data.stato); setNoteStudio(r.data.note_studio || "");
    }).catch(() => setP(false));
  }, [id]);
  useEffect(() => { load(); api.get("/credius/meta").then((r) => setMeta(r.data)).catch(() => {}); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  if (p === null) return <div className="flex justify-center py-20"><Loader2 className="w-9 h-9 animate-spin text-indigo-800" /></div>;
  if (p === false) return <div className="text-center py-20 text-slate-500">Pratica non trovata o accesso negato.</div>;

  const giud = p.tipo === "giudiziale";
  const incaricoStato = p.incarico_stato || "Accettato";
  const isClient = !!p._is_client_view;
  const isAvvocatoAssegnato = !isAdmin && !isClient;  // fornitore assegnato alla pratica
  const operativo = !isClient && (isAdmin || incaricoStato === "Accettato");

  // ---- Card come blocchi riordinabili: lato Studio (gestionale) vs lato Avvocato (operativo) ----
  const dettagliCard = (
    <Card title="Scheda pratica" icon={ClipboardList} testid="credius-dettagli-card">
      <div className="space-y-3">
        <div>
          <div className="text-[11px] font-bold uppercase text-slate-500">Oggetto</div>
          <div className="font-semibold text-slate-800">{p.oggetto}</div>
        </div>
        {p.descrizione && (
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500">Descrizione controversia</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{p.descrizione}</div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500">Avvocato affidatario</div>
            <div className="font-semibold text-slate-800">{p.avvocato_nome || "—"}</div>
          </div>
          {/* Iter122ag · co-affidatario: visualizzazione + editor inline per admin */}
          {(p.coaffidatario_nome || (isAdmin && p.avvocato_fornitore_id)) && (
            <div data-testid="credius-coaffidatario-info">
              <div className="text-[11px] font-bold uppercase text-indigo-700">Co-affidatario</div>
              {!editCoaff ? (
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-800">{p.coaffidatario_nome || <span className="text-slate-400 italic">Nessuno</span>}</div>
                  {isAdmin && (
                    <button type="button" data-testid="credius-coaffidatario-edit"
                      onClick={() => { setCoaffSel(p.coaffidatario_fornitore_id || ""); setEditCoaff(true); }}
                      className="text-xs font-semibold text-indigo-700 hover:underline">
                      {p.coaffidatario_fornitore_id ? "Cambia / rimuovi" : "Aggiungi"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <select data-testid="credius-coaffidatario-select" value={coaffSel}
                    onChange={(e) => setCoaffSel(e.target.value)}
                    className="border-2 border-slate-300 rounded-md px-2 py-1 text-sm">
                    <option value="">— Nessuno —</option>
                    {(meta?.avvocati || []).filter((a) => a.id !== p.avvocato_fornitore_id).map((a) => (
                      <option key={a.id} value={a.id}>{a.ragione_sociale}</option>
                    ))}
                  </select>
                  <button type="button" data-testid="credius-coaffidatario-save"
                    onClick={async () => {
                      try {
                        await api.put(`/credius/pratiche/${p.id}`, { coaffidatario_fornitore_id: coaffSel });
                        setEditCoaff(false);
                        load();
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aggiornamento fallito"); }
                    }}
                    className="text-xs font-bold px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700">Salva</button>
                  <button type="button" onClick={() => setEditCoaff(false)} className="text-xs text-slate-500 hover:underline">Annulla</button>
                </div>
              )}
            </div>
          )}
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500">Affidata il</div>
            {/* Iter122k24 · Task 4 · finché non c'è affido, mostra "Non affidata" invece della data creazione */}
            <div className="text-sm text-slate-700" data-testid="credius-affidata-il">
              {p.stato === "Da affidare" || !p.avvocato_fornitore_id
                ? <span className="italic text-slate-500">Non affidata</span>
                : fmt(p.affidata_at || p.created_at)}
            </div>
          </div>
          {!isClient && (
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500">Tipo di pratica</div>
            <div className="font-semibold" data-testid="credius-tipo-interna">
              {p.interna ? (
                <span className="inline-flex items-center gap-1 text-emerald-800"><Building2 className="w-4 h-4" /> Interna</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-indigo-800"><User className="w-4 h-4" /> Esterna</span>
              )}
            </div>
          </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {/* Iter122k24 · Task 2/3 · label "Cliente/i" e "Controparte/i" (era "Clienti"/"Controparti") */}
          <PartiList label="Cliente/i" parti={p.clienti} />
          <PartiList label="Controparte/i" parti={p.controparti} />
        </div>
      </div>
      {isAdmin && (
        <div className="actions-row flex flex-wrap gap-3 mt-4 pt-3 border-t border-slate-100">
          <Button data-testid="credius-modifica-btn" variant="outline" disabled={busy}
            onClick={() => setShowModifica(true)}
            className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
            <Pencil className="w-4 h-4 mr-1.5" /> Modifica pratica
          </Button>
          {p.stato === "Da affidare" && (
            <Button data-testid="credius-trasforma-preventivo-btn" variant="outline" disabled={busy}
              onClick={() => window.confirm(`Trasformare la pratica ${p.numero} in fase "Preventivo"? Servirà per predisporre un preventivo per il cliente prima dell'affido definitivo.`) &&
                run(async () => {
                  await api.post(`/credius/pratiche/${id}/trasforma-preventivo`);
                }, `Pratica ${p.numero} trasformata in fase Preventivo`)}
              className="border-sky-300 text-sky-800 hover:bg-sky-50">
              <FileText className="w-4 h-4 mr-1.5" /> Trasforma in Preventivo
            </Button>
          )}
          {!p.trasformata_in_id && !giud && (
            <Button data-testid="credius-trasforma-btn" variant="outline" disabled={busy}
              onClick={() => window.confirm(`Trasformare la pratica ${p.numero} in una pratica GIUDIZIALE? Dati e documenti verranno riportati nella nuova pratica.`) &&
                run(async () => {
                  const { data } = await api.post(`/credius/pratiche/${id}/trasforma-giudiziale`);
                  toast.success(`Creata pratica giudiziale ${data.numero}`);
                  navigate(`/credius/${data.id}`);
                })}
              className="border-purple-300 text-purple-800 hover:bg-purple-50">
              <Gavel className="w-4 h-4 mr-1.5" /> Trasforma in giudiziale
            </Button>
          )}
          {["Affidata", "In gestione"].includes(p.stato) && p.avvocato_fornitore_id && (
            <Button data-testid="credius-revoca-btn" variant="outline" disabled={busy}
              onClick={() => setShowRevoca(true)}
              className="border-red-300 text-red-800 hover:bg-red-50">
              <ShieldOff className="w-4 h-4 mr-1.5" /> Revoca affido
            </Button>
          )}
        </div>
      )}
    </Card>
  );

  const noteCard = isAdmin && (
    <Card title="Note interne di Studio (riservate)" icon={StickyNote} testid="credius-note-card">
      <p className="text-xs text-slate-400 mb-2">Appunti sulla gestione del rapporto con il cliente: NON visibili all'avvocato e senza traccia in cronologia.</p>
      <Textarea data-testid="credius-note-studio" rows={3} value={noteStudio}
        onChange={(e) => setNoteStudio(e.target.value)}
        placeholder="es. Cliente contattato il 10/07: concordato anticipo spese; sollecitare delibera assembleare..." />
      {noteStudio !== (p.note_studio || "") && (
        <div className="actions-row flex flex-wrap gap-3 mt-2">
          <Button size="sm" data-testid="credius-note-salva" disabled={busy}
            onClick={() => run(() => api.put(`/credius/pratiche/${id}`, { note_studio: noteStudio }), "Note interne salvate")}
            className="bg-indigo-900 hover:bg-indigo-950">Salva note</Button>
        </div>
      )}
    </Card>
  );

  // Iter122ak · Step 2 · unica card "Gestione pratica" che raccoglie attività + documentale
  // Iter122am · rimossa sezione "Avanzamento pratica": lo stato di affido si aggiorna
  // automaticamente in base agli eventi di ciclo di vita.
  // Iter122k24 · Task 5 · unificazione allegazione file: rimosso il pannello "Allega documenti"
  // dentro Registrazione attività (attività resta puramente testuale); la sezione Documentazione
  // è l'unico entry point per caricare file, con flag di visibilità cliente PER-FILE.
  const gestioneCard = operativo && (
    <Card title="Gestione pratica" icon={Scale} testid="credius-gestione-card">
      {/* --- Registrazione attività (solo testo) --- */}
      <div className="space-y-3" data-testid="credius-gestione-attivita">
        <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Registrazione attività
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500 mb-1 block">Registra un&apos;attività svolta</Label>
          <div className="flex gap-2">
            <Input data-testid="credius-attivita-input" value={attivita} onChange={(e) => setAttivita(e.target.value)}
              placeholder="es. Redatta e depositata comparsa di costituzione" className="h-11" />
            <Button data-testid="credius-attivita-add" disabled={busy || !attivita.trim()}
              onClick={() => run(() => api.post(`/credius/pratiche/${id}/attivita`, {
                testo: attivita,
                file_ids: [],
                visibile_cliente: attVisibile,
                tipo_documento: "Altro",
                parte: attParte,
              }).then(() => { setAttivita(""); setAttVisibile(true); setAttParte("parte"); }),
              "Attività registrata")}
              className="h-11 bg-indigo-900 hover:bg-indigo-950 shrink-0"><Plus className="w-5 h-5" /></Button>
          </div>
          {/* Iter122an · selettore Parte / Controparte */}
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="credius-attivita-parte-wrap">
            <span className="text-[10px] font-bold uppercase text-slate-500">Attribuzione:</span>
            <button type="button" data-testid="credius-attivita-parte-parte"
              onClick={() => setAttParte("parte")}
              className={`text-[11px] font-bold px-2 py-1 rounded-full border transition-colors ${attParte === "parte" ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}>
              ● Parte (avvocato/cliente/studio)
            </button>
            <button type="button" data-testid="credius-attivita-parte-controparte"
              onClick={() => setAttParte("controparte")}
              className={`text-[11px] font-bold px-2 py-1 rounded-full border transition-colors ${attParte === "controparte" ? "bg-red-600 text-white border-red-700" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}>
              ● Controparte
            </button>
            {!p.interna && (
              <label className="flex items-center gap-1.5 text-[11px] text-amber-800 ml-2" data-testid="credius-attivita-visibilita-wrap">
                <input type="checkbox" checked={attVisibile} onChange={(e) => setAttVisibile(e.target.checked)}
                  className="w-3.5 h-3.5 accent-emerald-600" data-testid="credius-attivita-visibile-cliente" />
                <span><b>Visibile al cliente esterno</b></span>
              </label>
            )}
          </div>
          <p className="text-[10px] text-slate-500 mt-1 italic">Per allegare file usa la sezione &quot;Documentazione&quot; sottostante.</p>
        </div>
      </div>

      <div className="border-t border-slate-200 my-4"></div>

      {/* --- Sezione documentale --- */}
      {/* Iter122k30 · sezione collassabile (menu a tendina), rimossa AttachmentGallery (duplicava
          la lista file), pulsante "Allega documenti" spostato SOPRA la lista */}
      <div className="space-y-3" data-testid="credius-gestione-documentale">
        <button type="button" data-testid="credius-doc-toggle"
          onClick={() => setDocsSectionOpen((v) => !v)}
          className="w-full flex flex-wrap items-center justify-between gap-2 text-left">
          <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5">
            <FolderOpen className="w-3.5 h-3.5" /> Documentazione
            {(p.documenti || []).length > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{p.documenti.length}</span>
            )}
          </div>
          {docsSectionOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        {docsSectionOpen && (<>
        {/* Iter122k30 · pulsante "Allega documenti" spostato SOPRA la lista */}
        {!showUpload && operativo && (
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="credius-doc-add-btn" variant="outline" onClick={() => setShowUpload(true)}
              className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
              <Plus className="w-4 h-4 mr-1.5" /> Allega documenti
            </Button>
            {/* Iter122k102 · Crea cartella custom vuota (drop-target del DND) */}
            <Button data-testid="credius-doc-crea-cartella-btn" variant="outline"
              onClick={() => { setDocsGroupBy("cartella"); setCreatingFolder(true); }}
              className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
              <FolderPlus className="w-4 h-4 mr-1.5" /> Crea cartella
            </Button>
            {/* Iter123k266 · Rigenera "Elenco morosi" se la pratica ha almeno una
                controparte di tipo condòmino. Il PDF+Excel vengono allegati come
                nuovi documenti (i precedenti restano in storico per confronto). */}
            {isAdmin && (p.controparti || []).some((c) => c.tipo === "condomino") && (
              <Button data-testid="credius-doc-rigenera-elenco-morosi" variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/credius/pratiche/${p.id}/elenco-morosi`);
                    toast.success(`Elenco morosi rigenerato · ${data.numero_morosi} condòmini (PDF + Excel)`);
                    load();
                  } catch (e) {
                    toast.error(formatApiError(e?.response?.data?.detail) || "Rigenerazione non riuscita");
                  }
                }}
                className="border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Rigenera elenco morosi
              </Button>
            )}
          </div>
        )}
        {/* Iter122k70 · Modalità raggruppamento cartelle: TIPO · SOGGETTO · CARTELLA custom */}
        {((p.documenti || []).length > 0 || (p.documenti_cartelle || []).length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="credius-doc-groupby">
            <span className="text-[10px] font-bold uppercase text-slate-500 mr-1">Cartelle:</span>
            {[
              { k: "tipo", lbl: "Per tipo" },
              { k: "soggetto", lbl: "Per soggetto" },
              { k: "cartella", lbl: "Cartelle custom" },
            ].map((m) => (
              <button key={m.k} type="button" data-testid={`credius-doc-groupby-${m.k}`}
                onClick={() => { setDocsGroupBy(m.k); setOpenFolders({}); }}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${docsGroupBy === m.k ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                {m.lbl}
              </button>
            ))}
            <span className="ml-auto text-[10px] text-slate-500 italic hidden sm:inline"
              data-testid="credius-doc-dnd-hint">
              💡 Trascina i file su un'altra cartella per spostarli
            </span>
          </div>
        )}
        {/* Iter123k181 · Hint sottocartelle visibile solo in modalità "Cartelle custom" */}
        {docsGroupBy === "cartella" && (p.documenti_cartelle || []).length + (p.documenti || []).filter((d) => d.cartella).length > 0 && (
          <div className="text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-md px-2.5 py-1.5"
            data-testid="credius-doc-subfolder-hint">
            📁 <b>Sottocartelle</b>: clicca sull'icona <FolderPlus className="w-3 h-3 inline-block -mt-0.5 text-indigo-700" /> accanto a una cartella per crearne una annidata (es. <code className="bg-white px-1 rounded">Fatture / 2027</code>).
          </div>
        )}
        {/* Iter122k158 · Barra selezione multipla + Iter122k160 · Master GLOBALE sempre visibile.
            Sempre presente quando c'è almeno un documento nella pratica. */}
        {((p.documenti || []).length > 0) && (() => {
          const allDocIds = (p.documenti || []).map((d) => d.id);
          const nSel = allDocIds.filter((id) => selectedDocs.has(id)).length;
          const allSel = nSel === allDocIds.length && allDocIds.length > 0;
          const someSel = nSel > 0 && !allSel;
          return (
            <div className={`flex flex-wrap items-center gap-2 px-3 py-2 rounded-md border-2 ${nSel > 0 ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-slate-50"}`}
                 data-testid="credius-doc-selection-bar">
              <input type="checkbox"
                data-testid="credius-doc-select-all-global"
                checked={allSel}
                ref={(el) => { if (el) el.indeterminate = someSel; }}
                onChange={() => setSelectedDocs((s) => {
                  const n = new Set(s);
                  if (allSel) allDocIds.forEach((id) => n.delete(id));
                  else allDocIds.forEach((id) => n.add(id));
                  return n;
                })}
                title={allSel ? "Deseleziona tutti i documenti della pratica" : "Seleziona tutti i documenti della pratica"}
                className="shrink-0 w-4 h-4 accent-indigo-700 cursor-pointer" />
              <span className={`text-sm font-bold ${nSel > 0 ? "text-indigo-900" : "text-slate-700"}`}
                data-testid="credius-doc-selection-count">
                {nSel > 0
                  ? `${nSel}/${allDocIds.length} documento${allDocIds.length !== 1 ? "i" : ""} selezionat${nSel !== 1 ? "i" : "o"}`
                  : `Seleziona tutti (${allDocIds.length})`}
              </span>
              <div className="ml-auto flex items-center gap-2">
                {nSel > 0 && (
                  <Button size="sm" variant="outline" onClick={clearDocSelection}
                    data-testid="credius-doc-selection-clear"
                    className="h-8 px-2 text-xs">
                    Deseleziona
                  </Button>
                )}
                <Button size="sm" onClick={scaricaZipSelezionati}
                  data-testid="credius-doc-selection-zip"
                  disabled={zipDownloading || nSel === 0}
                  className="h-8 px-3 text-xs bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50">
                  {zipDownloading
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Preparo…</>
                    : <><Download className="w-3.5 h-3.5 mr-1" />Scarica ZIP</>}
                </Button>
              </div>
            </div>
          );
        })()}
        {(p.documenti || []).length === 0 && (!p.documenti_cartelle || p.documenti_cartelle.length === 0) && !showUpload && (
          <div className="text-sm text-slate-400">Nessun documento presente (procura, preventivo al cliente, lettera di incarico, atti...).</div>
        )}
        {/* Iter122k70 · Raggruppa i doc per la modalità selezionata; solo cartelle non vuote.
            Iter122k102 · In modalità "cartella" includi anche le cartelle vuote da `documenti_cartelle`. */}
        {((p.documenti || []).length > 0 || (docsGroupBy === "cartella" && (p.documenti_cartelle || []).length > 0)) && (() => {
          const docsAll = p.documenti || [];
          const groupKeyOf = (d) => {
            if (docsGroupBy === "tipo") return d.tipo || "Altro";
            if (docsGroupBy === "soggetto") {
              const nome = (d.soggetto_nome || "").trim();
              const prefix = (d.parte || "parte") === "controparte" ? "Controparte" : "Parte";
              return nome ? `${prefix}: ${nome}` : `${prefix} (senza nominativo)`;
            }
            return (d.cartella || "").trim() || "Non archiviati";
          };
          const groups = {};
          for (const d of docsAll) {
            const k = groupKeyOf(d);
            (groups[k] = groups[k] || []).push(d);
          }
          // Iter122k102 · aggiunge le cartelle vuote quando siamo in mode "cartella"
          if (docsGroupBy === "cartella") {
            for (const c of (p.documenti_cartelle || [])) {
              const name = (c || "").trim();
              if (name && !groups[name]) groups[name] = [];
            }
          }
          const entries = Object.entries(groups).sort((a, b) => {
            // Iter123k180 · ordina gerarchicamente così parent appare prima dei suoi children.
            // Modalità "cartella": split per '/' e confronta segmento per segmento.
            if (docsGroupBy !== "cartella") return a[0].localeCompare(b[0], "it");
            const sa = a[0].split("/"); const sb = b[0].split("/");
            for (let i = 0; i < Math.min(sa.length, sb.length); i++) {
              const c = sa[i].localeCompare(sb[i], "it");
              if (c !== 0) return c;
            }
            return sa.length - sb.length;
          });
          return (
            <div className="space-y-1.5" data-testid="credius-doc-folders">
              {entries.map(([folderName, folderDocs]) => {
                const key = `${docsGroupBy}:${folderName}`;
                const open = !!openFolders[key];
                const isControparte = folderName.startsWith("Controparte");
                // Iter123k180 · profondità per indentazione (modalità cartella)
                const depth = docsGroupBy === "cartella" && folderName !== "Non archiviati"
                  ? folderName.split("/").length - 1 : 0;
                const displayName = depth > 0
                  ? folderName.split("/").slice(-1)[0]
                  : folderName;
                // Iter122k74 · rinomina consentita solo su cartelle custom reali (non "Non archiviati")
                const canRenameFolder = docsGroupBy === "cartella" && folderName !== "Non archiviati" && (
                  isAdmin || folderDocs.every((d) => d.caricato_ruolo === "Fornitore")
                );
                return (
                  <div key={key} className={`rounded-lg border bg-white transition-colors ${dragOverKey === key ? "border-indigo-500 border-2 bg-indigo-50" : "border-slate-200"} ${depth > 0 ? "border-l-4 border-l-indigo-400" : ""}`}
                    style={depth > 0 ? { marginLeft: `${Math.min(depth, 4) * 20}px` } : undefined}
                    data-testid={`credius-doc-folder-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    onDragOver={(e) => {
                      if (draggingDocId) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverKey(key); }
                    }}
                    onDragLeave={() => { if (dragOverKey === key) setDragOverKey(null); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const docId = e.dataTransfer.getData("text/x-credius-doc") || draggingDocId;
                      if (docId) dropOnFolder(docId, folderName, docsGroupBy);
                    }}>
                    <div className={`w-full flex items-center justify-between gap-2 px-3 py-2 ${open ? "border-b border-slate-200" : ""}`}>
                      {/* Iter122k159 · Checkbox master per selezionare/deselezionare tutti i doc della cartella */}
                      {folderDocs.length > 0 && (() => {
                        const folderDocIds = folderDocs.map((d) => d.id);
                        const nSelected = folderDocIds.filter((id) => selectedDocs.has(id)).length;
                        const allSelected = nSelected === folderDocIds.length;
                        const someSelected = nSelected > 0 && !allSelected;
                        return (
                          <input type="checkbox"
                            data-testid={`credius-doc-folder-check-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            checked={allSelected}
                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => setSelectedDocs((s) => {
                              const n = new Set(s);
                              if (allSelected) folderDocIds.forEach((id) => n.delete(id));
                              else folderDocIds.forEach((id) => n.add(id));
                              return n;
                            })}
                            title={allSelected ? "Deseleziona tutti" : "Seleziona tutti i documenti della cartella"}
                            className="shrink-0 w-4 h-4 accent-indigo-700 cursor-pointer" />
                        );
                      })()}
                      <button type="button" onClick={() => toggleFolder(key)}
                        className="flex-1 flex items-center gap-2 min-w-0 text-left"
                        data-testid={`credius-doc-folder-toggle-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                        {open ? <FolderOpen className="w-4 h-4 text-indigo-700 shrink-0" /> : <Folder className="w-4 h-4 text-indigo-700 shrink-0" />}
                        <span className={`text-sm font-bold truncate ${isControparte ? "text-red-800" : "text-slate-800"}`}>{displayName}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0">{folderDocs.length}</span>
                      </button>
                      <div className="flex items-center gap-1 shrink-0">
                        {/* Iter123k180 · Crea sottocartella (solo mode cartella + solo top folder reale) */}
                        {docsGroupBy === "cartella" && folderName !== "Non archiviati" && (
                          <button type="button"
                            data-testid={`credius-doc-folder-add-sub-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            onClick={(e) => { e.stopPropagation(); setCreatingSubIn(folderName); setCreatingFolder(true); }}
                            title={`Crea sottocartella in «${folderName}»`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200 text-[10px] font-bold">
                            <FolderPlus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sottocartella</span>
                          </button>
                        )}
                        {canRenameFolder && (
                          <button type="button"
                            data-testid={`credius-doc-folder-rename-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            onClick={(e) => { e.stopPropagation(); setRenamingFolder(folderName); }}
                            title="Rinomina cartella"
                            className="p-1 rounded text-indigo-700 hover:bg-indigo-50">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {/* Iter122k102 · Elimina cartella vuota (solo mode custom + solo se 0 doc) */}
                        {docsGroupBy === "cartella" && folderName !== "Non archiviati" && folderDocs.length === 0 && isAdmin && (
                          <button type="button"
                            data-testid={`credius-doc-folder-delete-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            onClick={(e) => { e.stopPropagation(); deleteCartella(folderName); }}
                            title="Elimina cartella vuota"
                            className="p-1 rounded text-red-700 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button type="button" onClick={() => toggleFolder(key)} className="p-1 text-slate-500">
                          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    {open && (
                      <div className="divide-y divide-slate-100 text-xs text-slate-500">
                        {folderDocs.length === 0 && (
                          <div data-testid={`credius-doc-folder-empty-${folderName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            className="py-3 px-3 text-center text-slate-400 italic">
                            Cartella vuota — trascina qui i file da spostare, o usa "Allega documenti" e seleziona questa cartella.
                          </div>
                        )}
                        {folderDocs.map((d) => {
                          const docControparte = (d.parte || "parte") === "controparte";
                          const isDraggable = canMoveDoc(d);
                          return (
                            <div key={d.id} data-parte={d.parte || "parte"}
                              draggable={isDraggable}
                              onDragStart={(e) => {
                                setDraggingDocId(d.id);
                                e.dataTransfer.setData("text/x-credius-doc", d.id);
                                e.dataTransfer.effectAllowed = "move";
                              }}
                              onDragEnd={() => { setDraggingDocId(null); setDragOverKey(null); }}
                              data-testid={`credius-doc-row-${d.id}`}
                              className={`py-1.5 px-3 flex items-center gap-2 border-l-[3px] ${docControparte ? "border-red-400" : "border-emerald-400"} ${isDraggable ? "cursor-grab active:cursor-grabbing" : ""} ${draggingDocId === d.id ? "opacity-40" : ""}`}
                              title={isDraggable ? "Trascina per spostare in un'altra cartella" : undefined}>
                              {/* Iter122k158 · checkbox multi-selezione per download ZIP */}
                              <input type="checkbox"
                                data-testid={`credius-doc-check-${d.id}`}
                                checked={selectedDocs.has(d.id)}
                                onChange={() => toggleDocSelected(d.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="shrink-0 w-4 h-4 accent-indigo-700 cursor-pointer"
                                title="Seleziona per download ZIP" />
                              <button type="button" onClick={() => openFile(d)}
                                data-testid={`credius-doc-open-${d.id}`}
                                className="font-semibold text-slate-700 hover:text-indigo-800 truncate max-w-[200px] text-left underline-offset-2 hover:underline">
                                {d.original_filename}
                              </button>
                              <span className={`px-1.5 py-0.5 rounded font-semibold ${docControparte ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                                {docControparte ? "● Controparte" : "● Parte"}
                              </span>
                              <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">{d.tipo || "Altro"}</span>
                              {d.cartella && (
                                <span className="px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 font-semibold truncate max-w-[120px]" title={d.cartella}>📁 {d.cartella}</span>
                              )}
                              {d.soggetto_nome && (
                                <span className="px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 font-semibold truncate max-w-[120px]" title={d.soggetto_nome}>👤 {d.soggetto_nome}</span>
                              )}
                              {/* Iter122k107 · Indicatore verde/rosso visibilità al cliente esterno (cliccabile se admin/avvocato) */}
                              {(() => {
                                const shared = !d.riservato;
                                const canToggle = isAdmin || d.caricato_ruolo === "Fornitore";
                                const label = shared ? "Visibile al cliente" : "Non visibile al cliente";
                                const cls = shared
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                  : "bg-red-100 text-red-800 border border-red-300";
                                const iconChar = shared ? "✓" : "✗";
                                const onClick = canToggle
                                  ? async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await api.patch(`/credius/pratiche/${id}/documenti/${d.id}/visibilita`,
                                                         { visibile_cliente: !shared });
                                        toast.success(shared ? "Documento nascosto al cliente" : "Documento condiviso col cliente");
                                        load();
                                      } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Errore aggiornamento visibilità");
                                      }
                                    }
                                  : undefined;
                                return (
                                  <button type="button" onClick={onClick} disabled={!canToggle}
                                    data-testid={`credius-doc-visibilita-${d.id}`}
                                    data-visible={shared ? "true" : "false"}
                                    title={canToggle
                                      ? `${label} · click per invertire`
                                      : label}
                                    className={`px-1.5 py-0.5 rounded font-bold text-[10px] leading-tight ${cls} ${canToggle ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}>
                                    {iconChar} {shared ? "CLIENTE" : "RISERVATO"}
                                  </button>
                                );
                              })()}
                              <span className="hidden sm:inline">· {d.caricato_da} ({d.caricato_ruolo}) · {fmt(d.caricato_at)}</span>
                              <div className="ml-auto flex items-center gap-1">
                                {(isAdmin || d.caricato_ruolo === "Fornitore") && (
                                  <>
                                    <button type="button" data-testid={`credius-doc-move-${d.id}`} title="Sposta in cartella"
                                      onClick={() => setMovingDoc({ doc: d, cartella: d.cartella || "", soggetto_nome: d.soggetto_nome || "", parte: d.parte || "parte", tipo: d.tipo || "Altro" })}
                                      className="text-indigo-600 hover:text-indigo-800"><FolderInput className="w-4 h-4" /></button>
                                    <button type="button" data-testid={`credius-doc-del-${d.id}`}
                                      onClick={() => window.confirm("Rimuovere questo documento dalla pratica?") &&
                                        run(() => api.delete(`/credius/pratiche/${id}/documenti/${d.id}`), "Documento rimosso")}
                                      className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
        {showUpload && (
          <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-3">
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs uppercase text-slate-500">Tipo documento</Label>
                <select data-testid="credius-doc-tipo" value={docTipo} onChange={(e) => setDocTipo(e.target.value)} className={`${SEL} mt-1`}>
                  {(meta.tipi_documento || ["Altro"]).map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              {/* Iter122k70 · nominativo (parti/controparti già presenti in pratica) + cartella custom */}
              <div>
                <Label className="text-xs uppercase text-slate-500">Nominativo (opzionale)</Label>
                <input list="credius-doc-soggetti-list" data-testid="credius-doc-soggetto"
                  value={docSoggetto} onChange={(e) => setDocSoggetto(e.target.value)}
                  placeholder="— nessun soggetto —"
                  className={`${SEL} mt-1`} />
                <datalist id="credius-doc-soggetti-list">
                  {[...(p.clienti || []), ...(p.controparti || [])].map((s, i) => (
                    <option key={i} value={s.nome || ""} />
                  ))}
                </datalist>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase text-slate-500">Cartella (opzionale)</Label>
                {(() => {
                  // Iter122k158 · Bug fix: sostituito input+datalist (poco visibile
                  // e poco chiaro su mobile) con SELECT nativo che elenca sempre le
                  // cartelle esistenti + opzione "+ Crea nuova cartella".
                  const cartelleEsistenti = Array.from(new Set([
                    ...((p.documenti || []).map((d) => (d.cartella || "").trim())),
                    ...((p.documenti_cartelle || []).map((c) => (c.nome || "").trim())),
                  ].filter(Boolean))).sort();
                  const isCreating = docCartella === "__new__";
                  const known = cartelleEsistenti.includes(docCartella);
                  return (
                    <div className="mt-1 flex gap-2">
                      <select data-testid="credius-doc-cartella-select"
                        value={isCreating ? "__new__" : (known ? docCartella : (docCartella ? "__custom__" : ""))}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "__new__") { setDocCartella("__new__"); }
                          else if (v === "__custom__") { /* keep */ }
                          else { setDocCartella(v); }
                        }}
                        className={`${SEL} flex-1`}>
                        <option value="">— Nessuna cartella —</option>
                        {cartelleEsistenti.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                        <option value="__new__">+ Crea nuova cartella…</option>
                      </select>
                      {isCreating && (
                        <input type="text" data-testid="credius-doc-cartella-new"
                          autoFocus placeholder="Nome nuova cartella"
                          value=""
                          onChange={(e) => setDocCartella(e.target.value)}
                          onBlur={(e) => { if (!e.target.value) setDocCartella(""); }}
                          className={`${SEL} flex-1`} />
                      )}
                    </div>
                  );
                })()}
                <div className="text-[10px] text-slate-500 mt-1">Seleziona una cartella esistente o creane una nuova per raggruppare i documenti.</div>
              </div>
            </div>
            {/* Iter122an · classificazione parte / controparte del documento */}
            <div className="flex items-center gap-2 mb-3" data-testid="credius-doc-parte-wrap">
              <span className="text-[10px] font-bold uppercase text-slate-500">Attribuzione:</span>
              <button type="button" data-testid="credius-doc-parte-parte"
                onClick={() => setDocParte("parte")}
                className={`text-[11px] font-bold px-2 py-1 rounded-full border transition-colors ${docParte === "parte" ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}>
                ● Parte
              </button>
              <button type="button" data-testid="credius-doc-parte-controparte"
                onClick={() => setDocParte("controparte")}
                className={`text-[11px] font-bold px-2 py-1 rounded-full border transition-colors ${docParte === "controparte" ? "bg-red-600 text-white border-red-700" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}>
                ● Controparte
              </button>
            </div>
            <DocumentUploader docs={docs} onChange={setDocs} testid="credius-doc-uploader" />
            {/* Iter122k24 · Task 5 · flag "Visibile al cliente" per singolo file (opt-out per pratiche esterne) */}
            {docs.length > 0 && !p.interna && isAdmin && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid="credius-doc-visibilita-lista">
                <div className="text-[10px] font-bold uppercase text-amber-800 mb-1.5">Visibilità cliente esterno</div>
                <ul className="space-y-1.5">
                  {docs.map((d) => {
                    const visibile = docVisibilita[d.id] !== false; // default visibile
                    return (
                      <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate text-slate-700 font-semibold flex-1">{d.filename || d.original_filename || "Documento"}</span>
                        <label className="flex items-center gap-1.5 shrink-0 cursor-pointer" data-testid={`credius-doc-vis-toggle-${d.id}`}>
                          <input type="checkbox" checked={visibile}
                            onChange={(e) => setDocVisibilita((prev) => ({ ...prev, [d.id]: e.target.checked }))}
                            className="w-3.5 h-3.5 accent-emerald-600" />
                          <span className={visibile ? "text-emerald-700 font-bold" : "text-amber-800 font-bold"}>
                            {visibile ? "Visibile" : "Riservato"}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="actions-row flex flex-wrap gap-3 mt-3">
              <Button data-testid="credius-doc-salva" disabled={busy || docs.length === 0}
                onClick={async () => {
                  // Iter122k24 · Task 5 · invia due gruppi separati (visibili/riservati) mantenendo compatibilità con l'endpoint esistente
                  const visibileIds = docs.filter((d) => docVisibilita[d.id] !== false).map((d) => d.id);
                  const riservatoIds = docs.filter((d) => docVisibilita[d.id] === false).map((d) => d.id);
                  // Iter122k70 · include soggetto_nome + cartella custom per l'organizzazione in cartelle
                  const extra = { soggetto_nome: docSoggetto || "", cartella: docCartella || "" };
                  await run(async () => {
                    if (visibileIds.length > 0) {
                      await api.post(`/credius/pratiche/${id}/documenti`, { file_ids: visibileIds, tipo: docTipo, riservato: false, parte: docParte, ...extra });
                    }
                    if (riservatoIds.length > 0) {
                      await api.post(`/credius/pratiche/${id}/documenti`, { file_ids: riservatoIds, tipo: docTipo, riservato: true, parte: docParte, ...extra });
                    }
                    setDocs([]); setShowUpload(false); setDocVisibilita({}); setDocParte("parte");
                    setDocSoggetto(""); setDocCartella("");
                  }, "Documenti allegati");
                }}
                className="h-11 bg-indigo-900 hover:bg-indigo-950">Salva documenti</Button>
              <Button variant="outline" className="h-11" onClick={() => { setShowUpload(false); setDocs([]); setDocVisibilita({}); setDocParte("parte"); setDocSoggetto(""); setDocCartella(""); }}>Annulla</Button>
            </div>
          </div>
        )}
        {/* Iter122k30 · Chiusura del blocco collassabile (menu tendina) — il bottone
            "Allega documenti" è ora SOPRA la lista (vedi sopra), qui non serve. */}
        </>)}
      </div>
    </Card>
  );

  // Iter122ak · vista cliente esterno: sola sezione documentale (senza avanzamento editable)
  // Iter122k30 · rimossa AttachmentGallery (duplicava la lista file)
  const documentiCardCliente = (
    <Card title="Documentazione" icon={FolderOpen} testid="credius-documenti-card">
      {(p.documenti || []).length === 0 ? (
        <div className="text-sm text-slate-400 mb-2">Nessun documento presente.</div>
      ) : (
        <div className="divide-y divide-slate-100 text-xs text-slate-500">
          {p.documenti.map((d) => (
            <div key={d.id} className="py-1.5 flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => openFile(d)}
                data-testid={`credius-doc-cliente-open-${d.id}`}
                className="font-semibold text-slate-700 hover:text-indigo-800 truncate max-w-[220px] text-left underline-offset-2 hover:underline">
                {d.original_filename}
              </button>
              <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">{d.tipo || "Altro"}</span>
              <span className="hidden sm:inline">· {d.caricato_da} ({d.caricato_ruolo}) · {fmt(d.caricato_at)}</span>
              {/* Iter122k77 · pulsante "Carica firmato" — solo per il cliente e solo se il doc NON è già stato caricato dal cliente */}
              {d.caricato_ruolo !== "Cliente" && (
                <button type="button" data-testid={`credius-doc-cliente-sign-${d.id}`}
                  onClick={() => setClientUpload({ refDocId: d.id, refFilename: d.original_filename || "", files: [], nota: "", busy: false })}
                  className="ml-auto text-[11px] font-bold px-2 py-1 rounded-md border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100">
                  <FileSignature className="w-3 h-3 inline mr-1" /> Carica firmato
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Iter122k77 · pannello per caricare un documento firmato SENZA riferimento (es. carta d'identità firmata) */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <button type="button" data-testid="credius-doc-cliente-upload-toggle"
          onClick={() => setClientUpload((c) => c ? null : { refDocId: null, refFilename: "", files: [], nota: "", busy: false })}
          className="text-sm font-bold text-emerald-800 hover:text-emerald-900 flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Carica documento firmato
        </button>
        <div className="text-[11px] text-slate-500 mt-1">
          Scarica il documento (es. procura), firmalo e ricaricalo qui. Lo Studio e l'avvocato riceveranno una notifica automatica.
        </div>
      </div>
      {clientUpload && (
        <div className="mt-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3" data-testid="credius-doc-cliente-upload-panel">
          {clientUpload.refFilename ? (
            <div className="text-xs text-emerald-900 font-semibold mb-2">
              📄 Firma di: <span className="italic">{clientUpload.refFilename}</span>
            </div>
          ) : (
            <div className="text-xs text-emerald-900 font-semibold mb-2">Documento firmato (generico)</div>
          )}
          <DocumentUploader docs={clientUpload.files}
            onChange={(files) => setClientUpload((c) => ({ ...c, files }))}
            testid="credius-doc-cliente-uploader" />
          <div className="mt-2">
            <Label className="text-xs uppercase text-slate-500">Nota (opzionale)</Label>
            <Textarea value={clientUpload.nota}
              onChange={(e) => setClientUpload((c) => ({ ...c, nota: e.target.value }))}
              rows={2} maxLength={500}
              placeholder="Es. Procura firmata in data odierna"
              data-testid="credius-doc-cliente-upload-nota"
              className="mt-1" />
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <Button variant="outline" size="sm" onClick={() => setClientUpload(null)} disabled={clientUpload.busy}>
              Annulla
            </Button>
            <Button size="sm" data-testid="credius-doc-cliente-upload-invia"
              disabled={clientUpload.busy || clientUpload.files.length === 0}
              onClick={async () => {
                setClientUpload((c) => ({ ...c, busy: true }));
                try {
                  const { data } = await api.post(`/credius/pratiche/${id}/documenti/cliente`, {
                    file_ids: clientUpload.files.map((f) => f.id),
                    riferimento_doc_id: clientUpload.refDocId,
                    nota: clientUpload.nota,
                  });
                  toast.success(clientUpload.refFilename ? "Documento firmato inviato." : "Documento caricato.");
                  setP(data);
                  setClientUpload(null);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito");
                  setClientUpload((c) => ({ ...c, busy: false }));
                }
              }}
              className="bg-emerald-700 hover:bg-emerald-800">
              {clientUpload.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Invia</>}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );

  const attiCard = operativo && <CrediusAtti pratica={p} onGenerated={load} />;

  // Iter122aj · rimossi speseCard e pagamentiCard (Opzione A): la contabilità è gestita
  // interamente dalle Notule pro-forma, che al pagamento generano automaticamente le
  // fee_pending per il calcolo Ricorrenze. Gli endpoint backend spese/pagamenti restano
  // attivi per non perdere dati storici.

  // Iter103 · C4.3 · Notula pro-forma (avvocato o admin)
  // Iter122k105 · vista notule anche al CLIENTE (in lettura + azione "Conferma pagamento")
  const notulaCard = (operativo && (isAdmin || isAvvocatoAssegnato))
    ? <NotulaCard pratica={p} isAdmin={isAdmin} isClient={false} onReload={load} />
    : isClient
      ? <NotulaCard pratica={p} isAdmin={false} isClient={true} onReload={load} />
      : null;

  // Iter122k125 · Sezione "Recuperi" per pratiche di recupero crediti (settore = "Recupero crediti")
  const isRecuperoCrediti = (p.settore || "").trim().toLowerCase() === "recupero crediti";
  const recuperiCard = isRecuperoCrediti ? (
    <div className="bg-white rounded-xl border-2 border-red-200 p-4 sm:p-5" data-testid="credius-recuperi-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
          <Handshake className="w-5 h-5 text-red-700" />
        </div>
        <div>
          <h2 className="font-bold text-red-900">Recuperi</h2>
          <div className="text-xs text-slate-500">Posizioni debitorie, pagamenti e piani di rientro</div>
        </div>
      </div>
      <CrediusRecuperi pratica={p} />
    </div>
  ) : null;

  const chatCard = (
    <div className="bg-white rounded-xl border-2 border-slate-200 p-4 sm:p-5">
      <TicketMessaggi
        messaggi={p.messaggi || []}
        role={user?.role === "fornitore" ? "fornitore" : "admin"}
        variant="credius"
        endpoint={`/credius/pratiche/${id}/messaggi`}
        unreadCount={p.messaggi_non_letti || 0}
        onRead={() => api.post(`/credius/pratiche/${id}/messaggi/letti`).then((r) => setP(r.data)).catch(() => {})}
        onUpdate={(data) => setP(data)}
      />
    </div>
  );

  // Iter122al · Feed cronologico arricchito: mostra i documenti collegati e permette a
  // Studio/avvocato di togglare la visibilità cliente su singola voce (solo per operativi).
  const toggleVisibilitaEntry = async (entry) => {
    const nuovoStato = !entry.visibile_cliente;
    await run(() => api.patch(`/credius/pratiche/${id}/cronologia/${entry.id}/visibilita`,
      { visibile_cliente: nuovoStato }),
      nuovoStato ? "Voce ora visibile al cliente" : "Voce nascosta al cliente");
  };
  const toggleVisibilitaDoc = async (doc) => {
    const nuovoStato = !!doc.riservato; // se attualmente riservato, la rendo visibile
    await run(() => api.patch(`/credius/pratiche/${id}/documenti/${doc.id}/visibilita`,
      { visibile_cliente: nuovoStato }),
      nuovoStato ? "Documento ora visibile al cliente" : "Documento nascosto al cliente");
  };
  const docsMap = Object.fromEntries((p.documenti || []).map((d) => [d.id, d]));
  // Iter122ao · filtro feed cronologia
  const cronoAll = p.cronologia || [];
  const cronoParte = cronoAll.filter((h) => (h.parte || "parte") !== "controparte");
  const cronoContro = cronoAll.filter((h) => (h.parte || "parte") === "controparte");
  const cronoFiltered = feedFilter === "parte" ? cronoParte : feedFilter === "controparte" ? cronoContro : cronoAll;
  const cronologiaCard = (
    <Card title="Cronologia delle attività" icon={History} testid="credius-cronologia-card"
      collapsible defaultOpen={false} countBadge={cronoAll.length || null}>
      {/* Iter122ao · tab-filtro parte / controparte */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="credius-feed-filter">
        <button type="button" data-testid="credius-feed-filter-all"
          onClick={() => setFeedFilter("all")}
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${feedFilter === "all" ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
          Tutte <span className="opacity-70">({cronoAll.length})</span>
        </button>
        <button type="button" data-testid="credius-feed-filter-parte"
          onClick={() => setFeedFilter("parte")}
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${feedFilter === "parte" ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}>
          ● Parte <span className="opacity-70">({cronoParte.length})</span>
        </button>
        <button type="button" data-testid="credius-feed-filter-controparte"
          onClick={() => setFeedFilter("controparte")}
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${feedFilter === "controparte" ? "bg-red-600 text-white border-red-700" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}>
          ● Controparte <span className="opacity-70">({cronoContro.length})</span>
        </button>
      </div>
      {cronoFiltered.length === 0 ? (
        <div className="text-sm text-slate-400">{feedFilter === "all" ? "Nessuna attività registrata." : `Nessuna attività per filtro «${feedFilter === "parte" ? "Parte" : "Controparte"}».`}</div>
      ) : (
        <div className="space-y-2.5">
          {[...cronoFiltered].reverse().map((h) => {
            const key = h.id || `${h.at}-${h.action}`;
            const docCollegati = (h.documenti_ids || []).map((did) => docsMap[did]).filter(Boolean);
            const showToggle = operativo && !isClient;
            const visibile = h.visibile_cliente !== false; // default true per entries legacy
            // Iter122an · colore laterale in base a parte (verde) / controparte (rosso)
            const isControparte = (h.parte || "parte") === "controparte";
            const borderColor = isControparte ? "border-red-400" : "border-emerald-400";
            return (
              <div key={key} data-testid={`credius-feed-entry-${h.id || key}`}
                data-parte={h.parte || "parte"}
                className={`flex gap-3 text-sm border-l-[3px] ${borderColor} pl-3`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-800">{h.action}</span>
                    {/* Iter122an · badge parte/controparte */}
                    <span data-testid={`credius-feed-parte-${h.id || key}`}
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isControparte ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {isControparte ? "● Controparte" : "● Parte"}
                    </span>
                    {showToggle && (
                      <button type="button" data-testid={`credius-feed-vis-${h.id}`}
                        disabled={!h.id || busy}
                        onClick={() => toggleVisibilitaEntry(h)}
                        title={visibile ? "Nascondi al cliente esterno" : "Mostra al cliente esterno"}
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${visibile ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100" : "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"} ${!h.id ? "opacity-50 cursor-not-allowed" : ""}`}>
                        {visibile ? "👁 CLIENTE" : "🔒 RISERVATA"}
                      </button>
                    )}
                    {!showToggle && !visibile && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">🔒 RISERVATA</span>
                    )}
                  </div>
                  {h.note && <div className="text-slate-600 text-xs mt-0.5">{h.note}</div>}
                  {docCollegati.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`credius-feed-docs-${h.id || key}`}>
                      {docCollegati.map((d) => {
                        const docControparte = (d.parte || "parte") === "controparte";
                        return (
                        <div key={d.id} data-parte={d.parte || "parte"}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] ${docControparte ? "border-red-200 bg-red-50/40 text-red-900" : "border-emerald-200 bg-emerald-50/40 text-emerald-900"}`}>
                          <FileText className="w-3 h-3" />
                          <button type="button" onClick={() => openSecureFile(`/files/${d.id}/download`)}
                            className="font-semibold hover:underline truncate max-w-[140px]">
                            {d.original_filename}
                          </button>
                          {d.riservato && <span className="text-[9px] font-bold text-amber-700">🔒</span>}
                          {showToggle && (
                            <button type="button" data-testid={`credius-feed-doc-vis-${d.id}`}
                              disabled={busy}
                              onClick={() => toggleVisibilitaDoc(d)}
                              title={d.riservato ? "Mostra al cliente" : "Nascondi al cliente"}
                              className="text-[9px] px-0.5 rounded hover:bg-slate-100">
                              {d.riservato ? "👁" : "🔒"}
                            </button>
                          )}
                        </div>
                      );})}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-0.5">{h.by}{h.ruolo ? ` (${h.ruolo})` : ""} · {fmt(h.at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  return (
    <div data-testid="credius-detail-page" className="max-w-4xl mx-auto space-y-4">
      <button type="button" onClick={() => navigate("/credius")} data-testid="credius-detail-back"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-indigo-900">
        <ArrowLeft className="w-4 h-4" /> Torna a Credius
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-11 h-11 rounded-2xl bg-indigo-900 flex items-center justify-center shrink-0">
          {giud ? <Gavel className="w-6 h-6 text-[#D4AF5A]" /> : <Scale className="w-6 h-6 text-[#D4AF5A]" />}
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-indigo-950">{p.numero}</h1>
          <div className="text-sm text-slate-500 capitalize">Pratica {p.tipo}{p.settore ? ` · ${p.settore}` : ""}</div>
        </div>
        <span data-testid="credius-stato-badge" className={`text-sm font-bold px-3 py-1 rounded-full border ${STATO_STYLE[p.stato]}`}>{p.stato}</span>
        <button type="button" onClick={() => openSecureFile(`/credius/pratiche/${p.id}/pdf`)}
          data-testid="credius-scheda-pdf"
          className="ml-auto inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border-2 border-indigo-300 text-sm font-semibold text-indigo-800 hover:bg-indigo-50 shrink-0">
          <FileDown className="w-4 h-4" /> Scheda pratica (PDF)
        </button>
      </div>

      {p.trasformata_in_id && (
        <div data-testid="credius-trasformata-banner" className="rounded-xl border-2 border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
          Questa pratica è stata trasformata nella pratica giudiziale{" "}
          <button type="button" className="font-bold underline" onClick={() => navigate(`/credius/${p.trasformata_in_id}`)}>{p.trasformata_in_numero}</button>.
        </div>
      )}
      {p.origine_pratica_id && (
        <div data-testid="credius-origine-banner" className="rounded-xl border-2 border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          Generata dalla pratica stragiudiziale{" "}
          <button type="button" className="font-bold underline" onClick={() => navigate(`/credius/${p.origine_pratica_id}`)}>{p.origine_pratica_numero}</button>{" "}
          (dati e documenti ereditati).
        </div>
      )}

      {/* Iter96 · Banner "Da affidare" con form d'affido tardivo */}
      {p.stato === "Da affidare" && isAdmin && (
        <AffidoDaFareBanner pratica={p} meta={meta} onAffidata={load} />
      )}

      {/* Workflow di accettazione/rifiuto incarico */}
      {incaricoStato === "In attesa di accettazione" && !isAdmin && (
        <div data-testid="credius-incarico-banner" className="rounded-xl border-2 border-orange-300 bg-orange-50 p-4">
          <div className="font-bold text-orange-900">Incarico in attesa di accettazione</div>
          <p className="text-sm text-orange-800 mt-1">
            Lo Studio ti ha affidato questa pratica. Accetta l'incarico per iniziare a gestirla: la lettera di incarico
            verrà generata automaticamente e archiviata nella sezione documentale. In caso di rifiuto indica il motivo.
          </p>
          {rifiutoMode ? (
            <div className="mt-3 space-y-2">
              <Label className="text-xs uppercase text-orange-800">Motivo del rifiuto *</Label>
              <Textarea data-testid="credius-rifiuto-motivo" rows={2} value={rifiutoMotivo}
                onChange={(e) => setRifiutoMotivo(e.target.value)} className="bg-white"
                placeholder="es. Conflitto di interessi con la controparte" />
              <div className="actions-row flex flex-wrap gap-2">
                <Button data-testid="credius-rifiuta-conferma" disabled={busy || !rifiutoMotivo.trim()}
                  onClick={() => run(() => api.post(`/credius/pratiche/${id}/incarico`, { azione: "rifiuta", motivo: rifiutoMotivo })
                    .then(() => { setRifiutoMode(false); setRifiutoMotivo(""); }), "Incarico rifiutato: lo Studio è stato avvisato")}
                  className="h-11 bg-red-700 hover:bg-red-800">Conferma rifiuto</Button>
                <Button variant="outline" className="h-11" onClick={() => setRifiutoMode(false)}>Annulla</Button>
              </div>
            </div>
          ) : (
            <div className="actions-row flex flex-wrap gap-3 mt-3">
              <Button data-testid="credius-accetta-btn" disabled={busy}
                onClick={() => window.confirm(`Accettare l'incarico per la pratica ${p.numero}? Verrà generata la lettera di incarico.`) &&
                  run(() => api.post(`/credius/pratiche/${id}/incarico`, { azione: "accetta" }),
                    "Incarico accettato: lettera di incarico generata e archiviata")}
                className="h-11 bg-emerald-700 hover:bg-emerald-800 font-bold">Accetta incarico</Button>
              <Button data-testid="credius-rifiuta-btn" variant="outline" disabled={busy}
                onClick={() => setRifiutoMode(true)}
                className="h-11 border-red-300 text-red-700 hover:bg-red-50">Rifiuta incarico</Button>
            </div>
          )}
        </div>
      )}
      {incaricoStato === "In attesa di accettazione" && isAdmin && (
        <div data-testid="credius-incarico-attesa-admin" className="rounded-xl border-2 border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
          Incarico <b>in attesa di accettazione</b> da parte di {p.avvocato_nome}: l'avvocato è stato avvisato via email e notifica.
        </div>
      )}
      {incaricoStato === "Rifiutato" && (
        <div data-testid="credius-incarico-rifiutato-banner" className="rounded-xl border-2 border-red-300 bg-red-50 p-4">
          <div className="font-bold text-red-800">Incarico rifiutato{p.avvocato_nome ? ` da ${p.avvocato_nome}` : ""}</div>
          {p.incarico_rifiuto_motivo && <p className="text-sm text-red-700 mt-1">Motivo: {p.incarico_rifiuto_motivo}</p>}
          {isAdmin && (
            <div className="mt-3">
              <Label className="text-xs uppercase text-red-700">Riaffida la pratica a un altro avvocato</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1">
                <select data-testid="credius-riaffida-select" value={riaffidaId} onChange={(e) => setRiaffidaId(e.target.value)}
                  className="h-11 flex-1 border-2 border-slate-300 rounded-lg px-3 bg-white">
                  <option value="">— Seleziona avvocato —</option>
                  {(meta.avvocati || []).filter((a) => a.id !== p.avvocato_fornitore_id)
                    .map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
                </select>
                <Button data-testid="credius-riaffida-btn" disabled={busy || !riaffidaId}
                  onClick={() => run(() => api.put(`/credius/pratiche/${id}`, { avvocato_fornitore_id: riaffidaId })
                    .then(() => setRiaffidaId("")), "Pratica riaffidata: il nuovo avvocato è stato avvisato")}
                  className="h-11 bg-indigo-900 hover:bg-indigo-950 shrink-0">Riaffida</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ordine gestionale (Studio) vs ordine operativo (Avvocato) vs vista cliente C3b */}
      {isClient ? (
        <>
          {dettagliCard}
          {recuperiCard}
          {documentiCardCliente}
          {notulaCard}
          {chatCard}
          {cronologiaCard}
        </>
      ) : isAdmin ? (
        // Iter122ak · Tab switcher esteso a TUTTE le pratiche (interne ed esterne) viste dall'admin
        <div className="space-y-4" data-testid="credius-detail-tabs">
          <div className="flex flex-wrap items-center gap-1 border-b-2 border-slate-200">
            <button
              data-testid="credius-detail-tab-gestione"
              onClick={() => setDetailTab("gestione")}
              className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${detailTab === "gestione" ? "bg-indigo-50 border-indigo-500 text-indigo-800" : "border-transparent text-slate-500 hover:text-indigo-700 hover:bg-indigo-50/40"}`}
            >
              <Scale className="w-4 h-4" /> Gestione pratica
            </button>
            {isRecuperoCrediti && (
              <button
                data-testid="credius-detail-tab-diffide"
                onClick={() => setDetailTab("diffide")}
                className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${detailTab === "diffide" ? "bg-brand/10 border-brand text-brand-dark" : "border-transparent text-slate-500 hover:text-brand hover:bg-brand/5"}`}
              >
                <FileSignature className="w-4 h-4" /> Editor Diffide
              </button>
            )}
            {isRecuperoCrediti && (
              <button
                data-testid="credius-detail-tab-recuperi"
                onClick={() => setDetailTab("recuperi")}
                className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${detailTab === "recuperi" ? "bg-red-50 border-red-500 text-red-800" : "border-transparent text-slate-500 hover:text-red-700 hover:bg-red-50/40"}`}
              >
                <Handshake className="w-4 h-4" /> Recuperi
              </button>
            )}
            <button
              data-testid="credius-detail-tab-contabilita"
              onClick={() => setDetailTab("contabilita")}
              className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${detailTab === "contabilita" ? "bg-emerald-50 border-emerald-500 text-emerald-800" : "border-transparent text-slate-500 hover:text-emerald-700 hover:bg-emerald-50/40"}`}
            >
              <Wallet className="w-4 h-4" /> Contabilità
            </button>
          </div>
          {detailTab === "gestione" ? (
            <div className="space-y-4" data-testid="credius-detail-pane-gestione">
              {dettagliCard}
              {noteCard}
              {gestioneCard}
              {chatCard}
              {cronologiaCard}
            </div>
          ) : detailTab === "diffide" ? (
            <div className="space-y-4" data-testid="credius-detail-pane-diffide">
              <CrediusDiffide pratica={p} isAdmin={isAdmin} />
            </div>
          ) : detailTab === "recuperi" ? (
            <div className="space-y-4" data-testid="credius-detail-pane-recuperi">
              {recuperiCard}
            </div>
          ) : (
            <div className="space-y-4" data-testid="credius-detail-pane-contabilita">
              {notulaCard}
            </div>
          )}
        </div>
      ) : (
        <>
          {dettagliCard}
          {gestioneCard}
          {isRecuperoCrediti && operativo && (
            <div className="space-y-4" data-testid="credius-avvocato-pane-diffide">
              <CrediusDiffide pratica={p} isAdmin={false} />
            </div>
          )}
          {recuperiCard}
          {notulaCard}
          {chatCard}
          {cronologiaCard}
        </>
      )}

      {isAdmin && (
        <div className="actions-row flex flex-wrap gap-3 pb-6">
          <Button data-testid="credius-elimina-btn" variant="outline" disabled={busy}
            onClick={() => window.confirm(`Eliminare definitivamente la pratica ${p.numero}?`) &&
              run(async () => { await api.delete(`/credius/pratiche/${id}`); navigate("/credius"); }, "Pratica eliminata")}
            className="border-red-300 text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1.5" /> Elimina pratica
          </Button>
        </div>
      )}

      {showModifica && (
        <CrediusModificaPratica pratica={p} meta={meta}
          onSaved={(data) => { setShowModifica(false); setP(data); setNuovoStato(data.stato); setNoteStudio(data.note_studio || ""); }}
          onClose={() => setShowModifica(false)} />
      )}
      {showRevoca && (
        <RevocaAffidoModal pratica={p} onClose={() => setShowRevoca(false)}
          onRevocato={(data) => { setShowRevoca(false); setP(data); setNuovoStato(data.stato); }} />
      )}

      {/* Iter122k70 · Dialog "Sposta in cartella" */}
      {movingDoc && (
        <MoveDocDialog pratica={p} moving={movingDoc}
          onClose={() => setMovingDoc(null)}
          onMoved={(data) => { setMovingDoc(null); setP(data); }} />
      )}
      {/* Iter122k74 · Dialog "Rinomina cartella" */}
      {renamingFolder && (
        <RenameFolderDialog pratica={p} oldName={renamingFolder}
          onClose={() => setRenamingFolder(null)}
          onRenamed={(data) => { setRenamingFolder(null); setP(data); }} />
      )}
      {/* Iter122k102 · Dialog "Crea cartella" (vuota) */}
      {creatingFolder && (
        <CreateFolderDialog praticaId={id} pratica={p}
          parentFolder={creatingSubIn}
          onClose={() => { setCreatingFolder(false); setCreatingSubIn(null); }}
          onCreated={(data) => { setCreatingFolder(false); setCreatingSubIn(null); setP(data); setDocsGroupBy("cartella"); }} />
      )}
    </div>
  );
}

/* ===== Iter122k70 · Modale "Sposta documento in cartella" ===== */
function MoveDocDialog({ pratica, moving, onClose, onMoved }) {
  const [tipo, setTipo] = useState(moving.tipo || "Altro");
  const [parte, setParte] = useState(moving.parte || "parte");
  const [soggetto, setSoggetto] = useState(moving.soggetto_nome || "");
  const cartelleEsistenti = Array.from(new Set((pratica.documenti || [])
    .map((d) => (d.cartella || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "it"));
  // Iter122k74 · dropdown "Cartella" con opzioni fisse (— nessuna —, cartelle esistenti, ➕ Nuova cartella…)
  const initial = (moving.cartella || "").trim();
  const initialMode = initial ? "esistente" : "nessuna";
  const [cartellaMode, setCartellaMode] = useState(initialMode);
  const [cartellaSel, setCartellaSel] = useState(initial);
  const [cartellaNuova, setCartellaNuova] = useState("");
  const [busy, setBusy] = useState(false);

  const soggetti = [...(pratica.clienti || []), ...(pratica.controparti || [])];

  const submit = async () => {
    let cartella = "";
    if (cartellaMode === "esistente") cartella = (cartellaSel || "").trim();
    else if (cartellaMode === "nuova") cartella = (cartellaNuova || "").trim();
    setBusy(true);
    try {
      const { data } = await api.put(`/credius/pratiche/${pratica.id}/documenti/${moving.doc.id}/organizza`,
                                       { tipo, parte, soggetto_nome: soggetto, cartella });
      toast.success("Documento riorganizzato.");
      onMoved?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile spostare il documento");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="credius-doc-move-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FolderInput className="w-5 h-5 text-indigo-700" />
          <h3 className="text-lg font-bold">Sposta in cartella</h3>
        </div>
        <div className="text-xs text-slate-600 truncate">{moving.doc.original_filename}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Tipo</Label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1"
              data-testid="credius-doc-move-tipo">
              {["Altro", "Procura", "Preventivo", "Incarico", "Sospensione", "Rinuncia", "Documenti pratica"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Attribuzione</Label>
            <div className="flex gap-2 mt-1">
              <button type="button" data-testid="credius-doc-move-parte-p"
                onClick={() => setParte("parte")}
                className={`flex-1 text-xs font-bold px-2 py-2 rounded-lg border ${parte === "parte" ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>● Parte</button>
              <button type="button" data-testid="credius-doc-move-parte-c"
                onClick={() => setParte("controparte")}
                className={`flex-1 text-xs font-bold px-2 py-2 rounded-lg border ${parte === "controparte" ? "bg-red-600 text-white border-red-700" : "bg-red-50 text-red-700 border-red-200"}`}>● Controparte</button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Nominativo (opzionale)</Label>
            <input list="move-soggetti-list" data-testid="credius-doc-move-soggetto"
              value={soggetto} onChange={(e) => setSoggetto(e.target.value)}
              placeholder="— nessun soggetto —"
              className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1" />
            <datalist id="move-soggetti-list">
              {soggetti.map((s, i) => <option key={i} value={s.nome || ""} />)}
            </datalist>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Cartella personalizzata</Label>
            <select data-testid="credius-doc-move-cartella"
              value={cartellaMode === "nuova" ? "__NUOVA__" : (cartellaMode === "nessuna" ? "__NONE__" : cartellaSel)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__NONE__") { setCartellaMode("nessuna"); setCartellaSel(""); }
                else if (v === "__NUOVA__") { setCartellaMode("nuova"); setCartellaSel(""); }
                else { setCartellaMode("esistente"); setCartellaSel(v); }
              }}
              className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1">
              <option value="__NONE__">— nessuna cartella —</option>
              {cartelleEsistenti.map((c) => <option key={c} value={c}>📁 {c}</option>)}
              <option value="__NUOVA__">➕ Nuova cartella…</option>
            </select>
            {cartellaMode === "nuova" && (
              <input type="text" data-testid="credius-doc-move-cartella-nuova"
                value={cartellaNuova} onChange={(e) => setCartellaNuova(e.target.value)}
                placeholder="Nome nuova cartella"
                maxLength={80}
                className="h-11 w-full border-2 border-indigo-300 rounded-lg px-3 bg-white mt-2" />
            )}
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="credius-doc-move-conferma" onClick={submit} disabled={busy}
            className="bg-indigo-900 hover:bg-indigo-950">Sposta</Button>
        </div>
      </div>
    </div>
  );
}

/* ===== Iter122k74 · Modale "Rinomina cartella custom" ===== */
function RenameFolderDialog({ pratica, oldName, onClose, onRenamed }) {
  const [newName, setNewName] = useState(oldName);
  const [busy, setBusy] = useState(false);
  const cartelleEsistenti = Array.from(new Set((pratica.documenti || [])
    .map((d) => (d.cartella || "").trim()).filter(Boolean)));
  const docsInFolder = (pratica.documenti || []).filter((d) => (d.cartella || "").trim() === oldName);

  const submit = async () => {
    const v = (newName || "").trim();
    if (!v) { toast.error("Inserisci il nuovo nome della cartella"); return; }
    if (v === oldName) { toast.error("Il nuovo nome è uguale a quello corrente"); return; }
    if (cartelleEsistenti.includes(v) && !window.confirm(`La cartella «${v}» esiste già. Confermi la fusione dei documenti?`)) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/credius/pratiche/${pratica.id}/cartelle/rinomina`,
                                       { old_name: oldName, new_name: v });
      toast.success("Cartella rinominata.");
      onRenamed?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile rinominare la cartella");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="credius-doc-folder-rename-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-indigo-700" />
          <h3 className="text-lg font-bold">Rinomina cartella</h3>
        </div>
        <div className="text-xs text-slate-600">
          Cartella corrente: <b>«{oldName}»</b> · {docsInFolder.length} documento{docsInFolder.length === 1 ? "" : "i"}
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Nuovo nome</Label>
          <input type="text" autoFocus data-testid="credius-doc-folder-rename-input"
            value={newName} onChange={(e) => setNewName(e.target.value)}
            maxLength={80}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) submit(); }}
            className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1" />
          <div className="text-[10px] text-slate-500 mt-1">Se il nome coincide con un'altra cartella esistente, i documenti verranno uniti.</div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="credius-doc-folder-rename-conferma" onClick={submit} disabled={busy}
            className="bg-indigo-900 hover:bg-indigo-950">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rinomina"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ===== Iter97 · Fase C2: modale Revoca affido con motivo + opzione avallo cliente (per pratiche esterne) ===== */
function RevocaAffidoModal({ pratica, onClose, onRevocato }) {
  const [motivo, setMotivo] = useState("");
  const [avallo, setAvallo] = useState(false);
  const [busy, setBusy] = useState(false);
  const esterna = !pratica.interna;

  const submit = async () => {
    const m = motivo.trim();
    if (m.length < 4) { toast.error("Indica il motivo della revoca (minimo 4 caratteri)"); return; }
    if (!window.confirm(`Confermi la REVOCA dell'incarico a ${pratica.avvocato_nome}?\n\nDa quel momento l'avvocato non potrà più operare sulla pratica.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/credius/pratiche/${pratica.id}/revoca-affido`,
                                        { motivo: m, richiedi_avallo_cliente: esterna && avallo });
      toast.success("Incarico revocato — l'avvocato è stato notificato via email");
      onRevocato && onRevocato(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Revoca non riuscita");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="credius-revoca-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-800"><ShieldOff className="w-5 h-5" /></div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold text-red-900">Revoca affido — {pratica.numero}</h3>
            <p className="text-xs text-slate-600">Avvocato revocato: <b>{pratica.avvocato_nome}</b></p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><Plus className="w-6 h-6 rotate-45" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-slate-700">
            La pratica passerà in stato <b>&laquo;Da affidare&raquo;</b>. Potrai riassegnarla ad un altro avvocato o al medesimo (se sblocchi il blocco gestione).
          </div>
          <div>
            <Label className="text-sm">Motivo della revoca *</Label>
            <Textarea data-testid="credius-revoca-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
              rows={4} placeholder="es. Difficoltà comunicative · scadenze non rispettate · richiesta cliente..." />
          </div>
          {esterna && (
            <label className="flex items-start gap-2 p-3 rounded border-2 border-amber-200 bg-amber-50 cursor-pointer" data-testid="credius-revoca-avallo">
              <input type="checkbox" checked={avallo} onChange={(e) => setAvallo(e.target.checked)} className="w-4 h-4 mt-1 accent-amber-600" />
              <span className="text-sm text-amber-900">
                <b>Pratica esterna</b> — richiedi l&apos;avallo del cliente per la revoca. L&apos;avallo sarà raccolto tramite l&apos;app Cliente Credius (Fase C3).
              </span>
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
            <Button data-testid="credius-revoca-conferma" onClick={submit} disabled={busy || motivo.trim().length < 4}
              className="bg-red-700 hover:bg-red-800 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-1.5" />} Revoca affido
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Iter96 · Banner + form d'affido tardivo (pratica in stato "Da affidare") ===== */
function AffidoDaFareBanner({ pratica, meta, onAffidata }) {
  const [busy, setBusy] = useState(false);
  const [avv, setAvv] = useState("");
  // Iter122k24 · Task 0 · co-affidatario opzionale al momento dell'affido
  const [coavv, setCoavv] = useState("");
  const avvocati = meta?.avvocati || [];

  const affida = async () => {
    if (!avv) { toast.error("Seleziona l'avvocato affidatario"); return; }
    if (coavv && coavv === avv) { toast.error("Il co-affidatario deve essere diverso dall'affidatario"); return; }
    if (!window.confirm(`Affidare la pratica ${pratica.numero} all'avvocato selezionato${coavv ? " (con co-affidatario)" : ""}? Riceverà un'email di proposta incarico.`)) return;
    setBusy(true);
    try {
      await api.post(`/credius/pratiche/${pratica.id}/affida`, {
        avvocato_fornitore_id: avv,
        coaffidatario_fornitore_id: coavv || null,
      });
      toast.success("Pratica affidata: l'avvocato riceverà notifica via email");
      onAffidata && onAffidata();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Affido non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="credius-da-affidare-banner" className="rounded-xl border-2 border-slate-400 bg-slate-50 p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white shrink-0">
          <AlertCircle className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-slate-900">Pratica in attesa di affido</div>
          <p className="text-sm text-slate-700 mt-1">
            Questa pratica è stata salvata senza affido. Assegnala ora ad un avvocato convenzionato per attivare il workflow di proposta incarico.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Avvocato affidatario *</Label>
              <select data-testid="credius-affido-tardivo-select" value={avv} onChange={(e) => setAvv(e.target.value)} className={SEL}>
                <option value="">— Seleziona —</option>
                {avvocati.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Avvocato co-affidatario (facoltativo)</Label>
              <select data-testid="credius-affido-tardivo-coaffidatario" value={coavv} onChange={(e) => setCoavv(e.target.value)} className={SEL}>
                <option value="">— Nessuno —</option>
                {avvocati.filter((a) => a.id !== avv).map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <Button data-testid="credius-affido-tardivo-btn" onClick={affida} disabled={busy || !avv}
              className="h-11 px-5 bg-indigo-900 hover:bg-indigo-950 font-bold">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Affida
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Iter103 · C4.3 · NotulaCard: emissione notula pro-forma + workflow validazione
// Iter122k78 · Modifica/Elimina abilitati anche per l'avvocato che ha emesso la notula
function NotulaCard({ pratica, isAdmin, isClient = false, onReload }) {
  const { user } = useAuth();
  const [notule, setNotule] = useState([]);
  const [showForm, setShowForm] = useState(false);
  // Iter122k80 · spese_generali: importo € opzionale valorizzato manualmente dall'avvocato
  const [form, setForm] = useState({ compenso_imponibile: "", spese_esenti: "", descrizione: "", spese_generali: "" });
  // Iter122k78 · id notula in modifica (null = nuova)
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  // Iter122k105 · dialog conferma pagamento + upload fattura
  const [payingNotula, setPayingNotula] = useState(null); // notula in cui aprire il dialog "Conferma pagamento"
  const [fattNotula, setFattNotula] = useState(null); // idem per "Carica fattura"
  const pid = pratica.id;
  const load = () => api.get(`/credius/pratiche/${pid}/notule`).then((r) => setNotule(r.data)).catch(() => setNotule([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); onReload?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const resetForm = () => { setForm({ compenso_imponibile: "", spese_esenti: "", descrizione: "", spese_generali: "" }); setEditingId(null); setShowForm(false); };
  const emetti = async () => {
    const imp = parseFloat(form.compenso_imponibile);
    if (!imp || imp <= 0) { toast.error("Indicare un compenso imponibile > 0"); return; }
    const payload = {
      compenso_imponibile: imp,
      spese_esenti: parseFloat(form.spese_esenti) || 0,
      descrizione: form.descrizione,
      spese_generali: parseFloat(form.spese_generali) || 0,
    };
    if (editingId) {
      await run(() => api.put(`/credius/notule/${editingId}`, payload).then(resetForm), "Notula aggiornata");
    } else {
      await run(() => api.post(`/credius/pratiche/${pid}/notule`, payload).then(resetForm), "Notula pro-forma generata");
    }
  };
  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({
      compenso_imponibile: String(n.imponibile ?? n.compenso_imponibile ?? ""),
      spese_esenti: String(n.spese_esenti ?? ""),
      descrizione: n.descrizione || "",
      spese_generali: String(n.spese_generali ?? ""),
    });
    setShowForm(true);
  };
  const fmtEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
  const STATO_STYLE_N = {
    "Da autorizzare": "bg-amber-100 text-amber-800 border-amber-300",
    "Autorizzata": "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Respinta": "bg-red-100 text-red-800 border-red-300",
    "Pagata": "bg-slate-200 text-slate-700 border-slate-300",
  };
  // Iter122k78 · owner check per Modifica/Elimina (avvocato che l'ha emessa)
  // Iter122k105 · il cliente non può modificare né eliminare
  const canEditNotula = (n) => {
    if (isClient) return false;
    if (isAdmin) return true;
    const isOwner = n.emesso_by === user?.id;
    return isOwner && ["Da autorizzare", "Respinta"].includes(n.stato);
  };
  const canDeleteNotula = (n) => {
    if (isClient) return false;
    if (isAdmin) return n.stato !== "Pagata";
    return n.emesso_by === user?.id && n.stato === "Da autorizzare";
  };
  return (
    <Card title="Notule pro-forma (Avvocato → Studio)" icon={FileText} testid="credius-notule-card">
      <p className="text-xs text-slate-500 mb-3">
        L&apos;avvocato affidatario emette una notula pro-forma indicando compenso imponibile e spese esenti. Il calcolo di CPA, IVA e ritenuta d&apos;acconto avviene automaticamente in base al regime fiscale registrato in anagrafica.
      </p>
      {!isAdmin && !isClient && !showForm && (
        <Button data-testid="notula-nuova-btn" onClick={() => setShowForm(true)}
          className="mb-3 bg-indigo-900 hover:bg-indigo-950">
          <Plus className="w-4 h-4 mr-1" /> Genera nuova notula
        </Button>
      )}
      {showForm && (
        <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-3 space-y-3 mb-3">
          {editingId && (
            <div className="text-xs font-bold text-indigo-800 flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> Modifica notula esistente
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Compenso imponibile € *</Label>
              <Input type="number" step="0.01" min="0" data-testid="notula-imponibile"
                value={form.compenso_imponibile}
                onChange={(e) => setForm({ ...form, compenso_imponibile: e.target.value })}
                className="h-11 mt-1" />
            </div>
            <div>
              <Label>Spese esenti (art. 15) €</Label>
              <Input type="number" step="0.01" min="0" data-testid="notula-spese"
                value={form.spese_esenti}
                onChange={(e) => setForm({ ...form, spese_esenti: e.target.value })}
                className="h-11 mt-1" />
            </div>
          </div>
          <div>
            <Label>Descrizione / voci prestazione</Label>
            <Textarea rows={2} data-testid="notula-descrizione" value={form.descrizione}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
          </div>
          {/* Iter122k80 · Spese generali forfettarie (D.M. 55/2014 art. 2 c.2) — importo € manuale */}
          <div>
            <Label>Spese generali € (opzionale)</Label>
            <div className="flex gap-2 items-start mt-1">
              <Input type="number" step="0.01" min="0" data-testid="notula-spese-generali"
                value={form.spese_generali}
                onChange={(e) => setForm({ ...form, spese_generali: e.target.value })}
                placeholder="0,00"
                className="h-11 max-w-[180px]" />
              <button type="button" data-testid="notula-spese-generali-15"
                onClick={() => {
                  const imp = parseFloat(form.compenso_imponibile);
                  if (!imp || imp <= 0) { toast.error("Indica prima il compenso imponibile"); return; }
                  setForm({ ...form, spese_generali: (imp * 0.15).toFixed(2) });
                }}
                className="h-11 px-3 rounded-lg border-2 border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-xs font-bold">
                Calcola 15% dell&apos;imponibile
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Valorizzare solo se si intende esporre le spese generali forfettarie (D.M. 55/2014 art. 2 c.2). Se {'>'} 0 concorrono alla base CPA e IVA.</div>
          </div>
          <div className="actions-row flex flex-wrap gap-2">
            <Button data-testid="notula-genera" onClick={emetti} disabled={busy}
              className="bg-indigo-900 hover:bg-indigo-950">
              {editingId ? "Salva modifiche" : "Genera notula PDF"}
            </Button>
            <Button variant="outline" onClick={resetForm}>Annulla</Button>
          </div>
        </div>
      )}
      {notule.length === 0 ? (
        <div className="text-sm text-slate-400">Nessuna notula emessa.</div>
      ) : (
        <div className="space-y-2">
          {notule.map((n) => (
            <div key={n.id} data-testid={`notula-item-${n.id}`}
              className="bg-white rounded-lg border-2 border-slate-200 p-3 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE_N[n.stato] || "bg-slate-100"}`}>{n.stato}</span>
                <span className="font-mono text-xs text-slate-600">{n.codice_documento}</span>
                <span className="text-xs text-slate-500 ml-auto">Netto: <b>{fmtEur(n.totale_netto)}</b></span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>Imp: {fmtEur(n.imponibile)}</span>
                {n.spese_generali && n.spese_generali > 0 ? (
                  <span>· Spese gen.: {fmtEur(n.spese_generali)}</span>
                ) : null}
                <span>· CPA: {fmtEur(n.cpa)}</span>
                <span>· IVA: {fmtEur(n.iva)}</span>
                {n.ritenuta_acconto ? <span>· RA: -{fmtEur(n.ritenuta_acconto)}</span> : null}
                <span>· Regime: {n.regime_fiscale}</span>
              </div>
              <div className="actions-row flex flex-wrap gap-2 pt-1">
                <button type="button" onClick={() => openSecureFile(`/credius/notule/${n.id}/pdf`)}
                  className="text-xs font-semibold text-indigo-700 hover:underline"
                  data-testid={`notula-item-pdf-${n.id}`}>PDF</button>
                {isAdmin && n.stato === "Da autorizzare" && (
                  <>
                    <Button size="sm" data-testid={`notula-item-valida-${n.id}`} disabled={busy}
                      onClick={() => run(() => api.post(`/credius/notule/${n.id}/valida`), "Notula validata")}
                      className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800">✓ Valida</Button>
                    <Button size="sm" variant="outline" data-testid={`notula-item-respingi-${n.id}`} disabled={busy}
                      onClick={() => {
                        const m = window.prompt("Motivo del rigetto (min 4 caratteri):");
                        if (m && m.trim().length >= 4) run(() => api.post(`/credius/notule/${n.id}/respingi`, { motivo: m.trim() }), "Notula respinta");
                      }}
                      className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50">✕ Respingi</Button>
                  </>
                )}
                {!isAdmin && !isClient && n.stato === "Autorizzata" && (
                  <Button size="sm" data-testid={`notula-item-pagata-${n.id}`} disabled={busy}
                    onClick={() => setPayingNotula(n)}
                    className="h-7 text-xs bg-slate-700 hover:bg-slate-800">Conferma pagamento</Button>
                )}
                {/* Iter122k105 · CLIENTE conferma pagamento con data + ricevuta PDF */}
                {isClient && n.stato === "Autorizzata" && (
                  <Button size="sm" data-testid={`notula-cliente-paga-${n.id}`} disabled={busy}
                    onClick={() => setPayingNotula(n)}
                    className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800">💳 Segna pagata</Button>
                )}
                {/* Iter122k105 · AVVOCATO carica fattura elettronica su notula Pagata */}
                {!isAdmin && !isClient && n.stato === "Pagata" && !n.fattura_file_id && (
                  <Button size="sm" data-testid={`notula-carica-fattura-${n.id}`} disabled={busy}
                    onClick={() => setFattNotula(n)}
                    className="h-7 text-xs bg-indigo-800 hover:bg-indigo-900">📄 Carica fattura</Button>
                )}
                {/* Ricevuta + fattura scaricabili (a tutti) */}
                {n.ricevuta_file_id && (
                  <button type="button" onClick={() => openSecureFile(`/uploads/${n.ricevuta_file_id}`)}
                    className="text-[11px] font-semibold text-emerald-700 hover:underline"
                    data-testid={`notula-ricevuta-${n.id}`}>📎 Ricevuta pagamento</button>
                )}
                {n.fattura_file_id && (
                  <button type="button" onClick={() => openSecureFile(`/uploads/${n.fattura_file_id}`)}
                    className="text-[11px] font-semibold text-indigo-700 hover:underline"
                    data-testid={`notula-fattura-${n.id}`}>📄 Fattura {n.fattura_numero || "elettronica"}</button>
                )}
                {n.stato === "Respinta" && <span className="text-xs italic text-red-600">Motivo: {n.respinta_motivo}</span>}
                {/* Iter122k78 · Modifica notula — admin sempre; avvocato solo se propria e in stato modificabile */}
                {canEditNotula(n) && (
                  <Button size="sm" variant="outline" data-testid={`notula-item-modifica-${n.id}`}
                    onClick={() => startEdit(n)} disabled={busy || editingId === n.id}
                    className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
                  </Button>
                )}
                {canDeleteNotula(n) && (
                  <Button size="sm" variant="outline" data-testid={`notula-item-elimina-${n.id}`}
                    onClick={() => window.confirm(`Eliminare la notula ${n.codice_documento || n.id}? Cancellazione logica: recuperabile dal DB in caso di errore.`) &&
                      run(() => api.delete(`/credius/notule/${n.id}`), "Notula eliminata")}
                    className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Iter122k105 · Dialog Cliente/Admin conferma pagamento */}
      {payingNotula && (
        <PayNotulaDialog notula={payingNotula}
          onClose={() => setPayingNotula(null)}
          onDone={() => { setPayingNotula(null); load(); onReload?.(); }} />
      )}
      {/* Iter122k105 · Dialog Avvocato carica fattura elettronica */}
      {fattNotula && (
        <FatturaNotulaDialog notula={fattNotula}
          onClose={() => setFattNotula(null)}
          onDone={() => { setFattNotula(null); load(); onReload?.(); }} />
      )}
    </Card>
  );
}


/* ===== Iter122k105 · Dialog "Conferma pagamento" (cliente/admin) ===== */
function PayNotulaDialog({ notula, onClose, onDone }) {
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/uploads/documento", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileId(r.data.id); setFileName(r.data.filename || f.name);
      toast.success("Ricevuta caricata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/credius/notule/${notula.id}/pagata`, {
        data_pagamento: data, ricevuta_file_id: fileId, note,
      });
      toast.success("Pagamento confermato");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
        data-testid="pay-notula-dialog">
        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
          💳 Conferma pagamento notula {notula.codice_documento}
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Segna la notula come pagata indicando la data e (consigliato) allegando la ricevuta.
          L&apos;avvocato riceverà notifica per emettere la fattura elettronica.
        </p>
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Data pagamento</label>
        <Input type="date" data-testid="pay-notula-date" value={data}
          onChange={(e) => setData(e.target.value)} className="mt-1 h-10" />
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mt-3 block">Ricevuta / quietanza (PDF)</label>
        <div className="mt-1">
          {fileId ? (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs">
              <span className="text-emerald-700 truncate flex-1">📎 {fileName}</span>
              <button type="button" onClick={() => { setFileId(null); setFileName(""); }}
                className="text-red-600 text-xs">Rimuovi</button>
            </div>
          ) : (
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={upload}
              disabled={uploading} data-testid="pay-notula-file"
              className="text-xs" />
          )}
        </div>
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mt-3 block">Note (opz.)</label>
        <Textarea data-testid="pay-notula-note" value={note}
          onChange={(e) => setNote(e.target.value)} rows={2}
          placeholder="Es. Bonifico su IBAN...; assegno n....; contanti; ecc." />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button onClick={submit} disabled={busy || !data}
            data-testid="pay-notula-submit"
            className="bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : "💳"}
            Conferma pagata
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ===== Iter122k105 · Dialog "Carica fattura elettronica" (avvocato) ===== */
function FatturaNotulaDialog({ notula, onClose, onDone }) {
  const [numero, setNumero] = useState("");
  const [dataFatt, setDataFatt] = useState(new Date().toISOString().slice(0, 10));
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/uploads/documento", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileId(r.data.id); setFileName(r.data.filename || f.name);
      toast.success("Fattura caricata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    if (!fileId) { toast.error("Allega il file della fattura"); return; }
    setBusy(true);
    try {
      await api.post(`/credius/notule/${notula.id}/fattura`, {
        file_id: fileId, numero_fattura: numero.trim(), data_fattura: dataFatt,
      });
      toast.success("Fattura elettronica caricata e allegata alla pratica");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
        data-testid="fattura-notula-dialog">
        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
          📄 Carica fattura elettronica · {notula.codice_documento}
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Allega il PDF/XML della fattura elettronica emessa a fronte del pagamento incassato.
          Verrà agganciata automaticamente ai documenti della pratica (cartella «Fatture»).
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Numero fattura</label>
            <Input data-testid="fattura-numero" value={numero}
              onChange={(e) => setNumero(e.target.value)} placeholder="es. FE/2026/001" className="mt-1 h-10" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Data fattura</label>
            <Input type="date" data-testid="fattura-data" value={dataFatt}
              onChange={(e) => setDataFatt(e.target.value)} className="mt-1 h-10" />
          </div>
        </div>
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mt-3 block">File fattura elettronica *</label>
        <div className="mt-1">
          {fileId ? (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-xs">
              <span className="text-indigo-700 truncate flex-1">📎 {fileName}</span>
              <button type="button" onClick={() => { setFileId(null); setFileName(""); }}
                className="text-red-600 text-xs">Rimuovi</button>
            </div>
          ) : (
            <input type="file" accept=".pdf,.xml,.p7m" onChange={upload}
              disabled={uploading} data-testid="fattura-file"
              className="text-xs" />
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button onClick={submit} disabled={busy || !fileId}
            data-testid="fattura-submit"
            className="bg-indigo-800 hover:bg-indigo-900">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : "📄"}
            Allega fattura
          </Button>
        </div>
      </div>
    </div>
  );
}
function CreateFolderDialog({ praticaId, pratica, onClose, onCreated, parentFolder }) {
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  const esistenti = new Set([
    ...(pratica.documenti_cartelle || []).map((c) => (c || "").trim().toLowerCase()),
    ...(pratica.documenti || []).map((d) => (d.cartella || "").trim().toLowerCase()).filter(Boolean),
  ]);
  const submit = async () => {
    const n = (nome || "").trim();
    if (!n) { toast.error("Inserisci un nome"); return; }
    if (n.includes("/")) { toast.error("Il carattere '/' non è ammesso nel nome"); return; }
    if (n.toLowerCase() === "non archiviati") { toast.error("«Non archiviati» è un nome riservato"); return; }
    // Iter123k180 · componi il path completo quando siamo in sotto-cartella
    const fullPath = parentFolder ? `${parentFolder}/${n}` : n;
    if (esistenti.has(fullPath.toLowerCase())) { toast.error(`La cartella «${fullPath}» esiste già`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/credius/pratiche/${praticaId}/cartelle`, { nome: fullPath });
      toast.success(`Cartella «${fullPath}» creata`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creazione non riuscita");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
        data-testid="credius-doc-crea-cartella-dialog">
        <div className="flex items-center gap-2 mb-3">
          <FolderPlus className="w-5 h-5 text-indigo-800" />
          <h3 className="font-bold text-slate-800">
            {parentFolder ? `Nuova sottocartella in «${parentFolder}»` : "Crea nuova cartella"}
          </h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          {parentFolder
            ? "La sottocartella verrà creata vuota all'interno della cartella selezionata."
            : "La cartella verrà creata vuota. Puoi poi trascinare i file al suo interno o selezionarla in \"Allega documenti\"."}
        </p>
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">
          {parentFolder ? "Nome sottocartella" : "Nome cartella"}
        </label>
        <input type="text" data-testid="credius-doc-crea-cartella-input"
          value={nome} onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          maxLength={80} autoFocus
          placeholder={parentFolder ? "Es. 2027, Q1, Fascicolo A..." : "Es. Documentazione bancaria, Ordinanze, Corrispondenza..."}
          className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:border-indigo-500 text-sm" />
        {parentFolder && (
          <div className="mt-2 text-[11px] text-slate-500">
            Path finale: <code className="bg-slate-100 px-1 py-0.5 rounded">{parentFolder}/{nome || "..."}</code>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button onClick={submit} disabled={busy || !nome.trim()}
            data-testid="credius-doc-crea-cartella-submit"
            className="bg-indigo-900 hover:bg-indigo-950">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-1" />}
            Crea {parentFolder ? "sottocartella" : "cartella"}
          </Button>
        </div>
      </div>
    </div>
  );
}
